import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Store, ShoppingCart, Trophy, TrendingUp, ArrowRight, ArrowLeft,
  Star, Users, Zap, Shield, ChevronRight, ChevronLeft, Truck,
  Building2, MapPin, Globe, CheckCircle, BadgeCheck, Warehouse, CreditCard, Lock,
  Package, Scan, ThermometerSun, ShieldCheck, DollarSign, Target, Share2, Gift
} from 'lucide-react';
import { MarketplaceLogo } from '../components/MarketplaceLogos';
import MarketplaceCard from '../components/MarketplaceCard';
import AIChatPromo from '../components/AIChatPromo';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero slider - real product photos + S&S warehouse products
// Premium Advertisement Campaigns Data
const PREMIUM_ADS = [
  {
    id: 1,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/3886f011040bfc3b14b66bc206a3cc5b9ec67bfccc70db4b14d1f265c8f5252a.png',
    badge: 'NEW',
    badgeColor: 'bg-emerald-500',
    headline_ar: 'أناقة الصيف',
    headline_en: 'Summer Elegance',
    subheadline_ar: 'نظارات شمسية فاخرة',
    subheadline_en: 'Luxury Sunglasses',
    description_ar: 'اكتشفي تشكيلة النظارات الشمسية الفاخرة التي تجمع بين الأناقة والحماية',
    description_en: 'Discover our premium sunglasses collection',
    cta_ar: 'تسوقي الآن',
    cta_en: 'Shop Now',
    category: 'accessories',
    bgGradient: 'from-amber-50 via-orange-50 to-yellow-50',
  },
  {
    id: 2,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/23f910f233acea25528f7677eff68011344a979d0b1a3c094930b7555decb568.png',
    badge: 'WINTER',
    badgeColor: 'bg-blue-500',
    headline_ar: 'مجموعة الشتاء',
    headline_en: 'Winter Collection',
    subheadline_ar: 'معاطف فاخرة للمرأة العصرية',
    subheadline_en: 'Premium Coats for Modern Women',
    description_ar: 'دفء وأناقة في تصميم واحد، اكتشفي أحدث صيحات الموضة الشتوية',
    description_en: 'Warmth meets elegance in one design',
    cta_ar: 'اكتشفي المجموعة',
    cta_en: 'Discover Collection',
    category: 'fashion',
    bgGradient: 'from-slate-50 via-gray-50 to-stone-50',
  },
  {
    id: 3,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/1a7da1462a38c5b85475153b2d1a6fd511c4d5bbcf115a32cc955c949ab958b2.png',
    badge: 'KIDS',
    badgeColor: 'bg-pink-500',
    headline_ar: 'عالم المرح',
    headline_en: 'World of Fun',
    subheadline_ar: 'ألعاب تعليمية للأطفال',
    subheadline_en: 'Educational Toys for Kids',
    description_ar: 'امنح طفلك ساعات من المرح والتعلم مع ألعابنا المميزة',
    description_en: 'Give your child hours of fun and learning',
    cta_ar: 'تسوق ألعاب الأطفال',
    cta_en: 'Shop Kids Toys',
    category: 'toys',
    bgGradient: 'from-yellow-50 via-amber-50 to-orange-50',
  },
  {
    id: 4,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/cd5cf6612f869a6a8c96f7f72e9b8c1f4ba68ecf19539098974f0061717cd3ad.png',
    badge: 'MEN',
    badgeColor: 'bg-gray-800',
    headline_ar: 'أناقة الرجل',
    headline_en: 'Men\'s Elegance',
    subheadline_ar: 'أزياء رجالية فاخرة',
    subheadline_en: 'Premium Men\'s Fashion',
    description_ar: 'اكتشف أحدث صيحات الموضة الرجالية بأسلوب عصري وأنيق',
    description_en: 'Discover latest men\'s fashion trends',
    cta_ar: 'تسوق الآن',
    cta_en: 'Shop Now',
    category: 'men_fashion',
    bgGradient: 'from-slate-100 via-gray-50 to-zinc-50',
  },
  {
    id: 5,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/2f63fe867096cca439751ae2578e9658d97a9f23e236d1a02b1475e5faec00e4.png',
    badge: 'PETS',
    badgeColor: 'bg-orange-500',
    headline_ar: 'سعادة حيوانك',
    headline_en: 'Pet Happiness',
    subheadline_ar: 'ألعاب ومستلزمات الحيوانات',
    subheadline_en: 'Pet Toys & Supplies',
    description_ar: 'كل ما يحتاجه صديقك الوفي من ألعاب ومستلزمات عالية الجودة',
    description_en: 'Everything your loyal friend needs',
    cta_ar: 'تسوق للحيوانات',
    cta_en: 'Shop Pet Supplies',
    category: 'pet',
    bgGradient: 'from-orange-50 via-amber-50 to-yellow-50',
  },
  {
    id: 6,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/92efcf66809e16c7532e043ca1ddde3e2c61f3795845476129fe8401edd939d7.png',
    badge: '-30%',
    badgeColor: 'bg-red-500',
    headline_ar: 'حقائب فاخرة',
    headline_en: 'Luxury Handbags',
    subheadline_ar: 'تشكيلة حصرية من الجلد الطبيعي',
    subheadline_en: 'Exclusive Genuine Leather Collection',
    description_ar: 'حقائب يد فاخرة تعكس ذوقك الرفيع وأناقتك المميزة',
    description_en: 'Luxury handbags reflecting your refined taste',
    cta_ar: 'احصلي على خصمك',
    cta_en: 'Get Your Discount',
    category: 'accessories',
    bgGradient: 'from-amber-50 via-stone-50 to-orange-50',
  },
  {
    id: 7,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/eed99115d7a2dadea76106ce57a00072f4153576f5ac8649b250e6399dc84c4a.png',
    badge: 'TECH',
    badgeColor: 'bg-blue-600',
    headline_ar: 'التقنية الذكية',
    headline_en: 'Smart Technology',
    subheadline_ar: 'ساعات ذكية متطورة',
    subheadline_en: 'Advanced Smart Watches',
    description_ar: 'تابع صحتك ولياقتك مع أحدث الساعات الذكية بتصميم أنيق',
    description_en: 'Track your health with the latest smartwatches',
    cta_ar: 'اكتشف الساعات',
    cta_en: 'Discover Watches',
    category: 'electronics',
    bgGradient: 'from-slate-50 via-blue-50 to-indigo-50',
  },
  {
    id: 8,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/9e8db8c63cc3a1f5460fa39a041c6b869c865370bdc5244e6a0084b437ccb7c0.png',
    badge: 'PREMIUM',
    badgeColor: 'bg-amber-700',
    headline_ar: 'أحذية فاخرة',
    headline_en: 'Premium Shoes',
    subheadline_ar: 'جلد طبيعي بتصميم إيطالي',
    subheadline_en: 'Genuine Leather Italian Design',
    description_ar: 'خطوة واثقة مع أحذية من أجود أنواع الجلد الطبيعي',
    description_en: 'Confident steps with finest leather shoes',
    cta_ar: 'تسوق الأحذية',
    cta_en: 'Shop Shoes',
    category: 'men_fashion',
    bgGradient: 'from-amber-50 via-stone-50 to-yellow-50',
  },
  {
    id: 9,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/0e72b3194d6e16534fa093b6029c5bc3a5ea8acac8a356b4aa6ed6bc64df5877.png',
    badge: 'FITNESS',
    badgeColor: 'bg-green-600',
    headline_ar: 'قوة ولياقة',
    headline_en: 'Power & Fitness',
    subheadline_ar: 'معدات رياضية احترافية',
    subheadline_en: 'Professional Sports Equipment',
    description_ar: 'حقق أهدافك الرياضية مع أفضل معدات اللياقة البدنية',
    description_en: 'Achieve your fitness goals with the best equipment',
    cta_ar: 'ابدأ رحلتك',
    cta_en: 'Start Your Journey',
    category: 'sports',
    bgGradient: 'from-green-50 via-emerald-50 to-teal-50',
  },
  {
    id: 10,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/1efbccc0aa2e9d1a6adb8340577bc0f9d1f3a52518e089ecb95fb856b83b83bc.png',
    badge: 'TRAVEL',
    badgeColor: 'bg-sky-500',
    headline_ar: 'سافر بأناقة',
    headline_en: 'Travel in Style',
    subheadline_ar: 'حقائب سفر فاخرة',
    subheadline_en: 'Luxury Travel Luggage',
    description_ar: 'رحلات مريحة مع حقائب سفر عملية وأنيقة',
    description_en: 'Comfortable trips with practical elegant luggage',
    cta_ar: 'جهز لرحلتك',
    cta_en: 'Prepare for Your Trip',
    category: 'accessories',
    bgGradient: 'from-sky-50 via-blue-50 to-indigo-50',
  },
  {
    id: 11,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/8978c42a1c61414ecb2a48a7afc6984bc165a96f7e71d8432e14bf4a2992ad51.png',
    badge: 'BEAUTY',
    badgeColor: 'bg-pink-500',
    headline_ar: 'جمال طبيعي',
    headline_en: 'Natural Beauty',
    subheadline_ar: 'منتجات عناية بالبشرة',
    subheadline_en: 'Skincare Products',
    description_ar: 'اكتشفي أسرار البشرة المشرقة مع منتجاتنا الطبيعية',
    description_en: 'Discover secrets of radiant skin',
    cta_ar: 'اكتشفي الجمال',
    cta_en: 'Discover Beauty',
    category: 'beauty',
    bgGradient: 'from-pink-50 via-rose-50 to-red-50',
  },
  {
    id: 12,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/099bcec7765a96c410a04bcffeda3ee878b594a8f0584ec0fd309437df495268.png',
    badge: 'SCHOOL',
    badgeColor: 'bg-indigo-500',
    headline_ar: 'العودة للمدرسة',
    headline_en: 'Back to School',
    subheadline_ar: 'كل ما يحتاجه طفلك',
    subheadline_en: 'Everything Your Child Needs',
    description_ar: 'جهز أطفالك للعام الدراسي الجديد بأفضل المستلزمات',
    description_en: 'Prepare your kids for the new school year',
    cta_ar: 'تسوق للمدرسة',
    cta_en: 'Shop for School',
    category: 'baby',
    bgGradient: 'from-indigo-50 via-blue-50 to-violet-50',
  },
  {
    id: 13,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/9fc569746dbca9823450286679ceb620326348e33e878a362627c5a7a39d02a3.png',
    badge: 'VIP',
    badgeColor: 'bg-gradient-to-r from-amber-500 to-yellow-400',
    headline_ar: 'مجموعة VIP',
    headline_en: 'VIP Collection',
    subheadline_ar: 'حصري لعملائنا المميزين',
    subheadline_en: 'Exclusive for Our VIP Members',
    description_ar: 'تشكيلة محدودة من أرقى المنتجات الفاخرة لعملائنا VIP',
    description_en: 'Limited collection of finest luxury products',
    cta_ar: 'انضم لـ VIP',
    cta_en: 'Join VIP',
    category: 'accessories',
    bgGradient: 'from-amber-50 via-yellow-50 to-orange-50',
  },
  {
    id: 14,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/815b8daa59d1d78e78282d2ca5ffea43e0c5c176d441af3d56f0d708f79fcb2a.png',
    badge: 'FLASH',
    badgeColor: 'bg-red-600',
    headline_ar: 'تخفيضات خاطفة',
    headline_en: 'Flash Sale',
    subheadline_ar: 'خصومات تصل إلى 50%',
    subheadline_en: 'Up to 50% Off',
    description_ar: 'لا تفوت الفرصة! عروض محدودة على الإلكترونيات والأجهزة',
    description_en: 'Don\'t miss out! Limited deals on electronics',
    cta_ar: 'اشترِ الآن',
    cta_en: 'Buy Now',
    category: 'electronics',
    bgGradient: 'from-red-50 via-rose-50 to-pink-50',
  },
  {
    id: 15,
    image: 'https://static.prod-images.emergentagent.com/jobs/8e6de1f7-db9a-4728-881d-fddeef5bfa42/images/0d90f2ca9fef984f21ec2a6e1114649eb826b47056f485ac84076a7cfabc46e1.png',
    badge: 'GLAM',
    badgeColor: 'bg-fuchsia-500',
    headline_ar: 'تألقي دائماً',
    headline_en: 'Always Glamorous',
    subheadline_ar: 'مستحضرات تجميل فاخرة',
    subheadline_en: 'Luxury Cosmetics',
    description_ar: 'أبرزي جمالك الطبيعي مع أفضل مستحضرات التجميل العالمية',
    description_en: 'Highlight your natural beauty',
    cta_ar: 'تسوقي المكياج',
    cta_en: 'Shop Makeup',
    category: 'beauty',
    bgGradient: 'from-fuchsia-50 via-pink-50 to-rose-50',
  },
];

// Hero Slider - Uses current products from HERO_SLIDER array
const HERO_SLIDER = [
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/6c1d777c33e4d8f14da9f98f4b8470e95b980764c31c7cc4d51de1457132830a.png', label_ar: 'صنادل نسائية أنيقة', cat: 'fashion', label_en: 'women sandals shoes', badge: '-50%', badgeColor: 'bg-red-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/11c90f0dbb689ed063842e5eb07229ca8cc133719a4983ed2855b115158196f5.png', label_ar: 'نظارات شمسية', cat: 'accessories', label_en: 'sunglasses eyewear', badge: 'BEST', badgeColor: 'bg-amber-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/ea57328617ad4200c6c0f5888d93694cb2562a74062db44db7b70c2b0c1638c6.png', label_ar: 'ساعة فاخرة', cat: 'accessories', label_en: 'luxury watch', badge: 'VIP', badgeColor: 'bg-[#EAB308]' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/8ef108262740889b1dde5f39a7755eebe5a2e5bfa1d95e2ffd75a45dc4575270.png', label_ar: 'حقيبة يد فاخرة', cat: 'accessories', label_en: 'handbag designer bag', badge: 'NEW', badgeColor: 'bg-purple-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/41d8f7a11649a84e570f3ffcdf5e5e5d86c77d60ff447254548bde3db2524ba5.png', label_ar: 'سماعات لاسلكية', cat: 'electronics', label_en: 'wireless earbuds', badge: 'HOT', badgeColor: 'bg-orange-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/92ae875797dfcf14b9a76a6bdb49a0fcb4cb3bebc2b191c40adea0d79e4d8049.png', label_ar: 'ألعاب أطفال', cat: 'kids', label_en: 'kids toys', badge: 'SALE', badgeColor: 'bg-pink-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/844eaedf6ed793f3ca10f5a61178c6ffcfb6f636cdaf886e8d5a646e0ae23708.png', label_ar: 'حامل هاتف سيارة', cat: 'automotive', label_en: 'car phone mount', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/5b886f4ee0c2c2288820dd3596565058d4c459d6631bdb0cf4260365a20fc2be.png', label_ar: 'عناية بالبشرة', cat: 'beauty', label_en: 'skincare beauty', badge: '-30%', badgeColor: 'bg-pink-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/89416648517c23f93206a796e903c8fff6893da7774a1b6d6ef4cbee1fcb75b1.png', label_ar: 'مستلزمات حيوانات', cat: 'pets', label_en: 'pet supplies', badge: 'TOP', badgeColor: 'bg-emerald-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/60a6b6cc42730a5eb358902c8b60d70b714a703217799ec19df77838a09c3b1f.png', label_ar: 'ديكور منزلي', cat: 'home', label_en: 'home decor', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/820563b71923df743befd2ea8ccd5af6cbea8fd648792f39813d2828621e64d7.png', label_ar: 'أدوات حديقة', cat: 'garden', label_en: 'garden tools', badge: 'NEW', badgeColor: 'bg-green-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/85deba7ef8604848bb5bb0362043a6d9dd7a96ecd7a402849030fa211ee51b8e.png', label_ar: 'معدات رياضية', cat: 'sports', label_en: 'fitness sports', badge: 'TREND', badgeColor: 'bg-blue-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/a86c405b84d1176b5e45d074c4039de9ec4210173777b2b2643d9e8adc8a25ec.png', label_ar: 'محفظة رجالية', cat: 'accessories', label_en: 'mens wallet leather', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/41793eeca4a20f35d367aac8a5cbeea89d0e79efc61c9f9bc87cd1c126ac21a9.png', label_ar: 'سماعة ذكية', cat: 'electronics', label_en: 'smart speaker', badge: 'HOT', badgeColor: 'bg-orange-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/82f862508c1691789a22200c63f1a9876bf752c9f4453ec03286862ccedbcbcb.png', label_ar: 'وشاح حريري', cat: 'fashion', label_en: 'silk scarf women', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/962f636c6b52734a148441a5c64f217a16a191c7269884005321d15378ee97fd.png', label_ar: 'أدوات مطبخ', cat: 'home', label_en: 'kitchen utensils', badge: '-20%', badgeColor: 'bg-red-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/d6e78fbfb54af5474a6f784a67c3102486b48afc87e2effded1a322ad4f17637.png', label_ar: 'حذاء رياضي', cat: 'sports', label_en: 'running sneakers shoes', badge: 'BEST', badgeColor: 'bg-blue-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/dae699f8744b6da751b042c9d636a2ce07c4374af6ea932774c6902ba75b9499.png', label_ar: 'مجوهرات ذهبية', cat: 'jewelry', label_en: 'gold jewelry women', badge: 'VIP', badgeColor: 'bg-[#EAB308]' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/c82a7225f8cbd385d2d07d91cf2eb9dd1454c46e453d09da6d1a4a34cb2e119f.png', label_ar: 'مستلزمات أطفال', cat: 'kids', label_en: 'baby care supplies', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/079bf441028d42001bb68026a9b32c03de92745b98034a70ea7209264cecad1c.png', label_ar: 'سماعات قيمنق', cat: 'gaming', label_en: 'gaming headset', badge: 'HOT', badgeColor: 'bg-red-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/9777e6545e93bc4256c5d590851b6a8da5bad161273c2a98c5642f86464201ec.png', label_ar: 'حقيبة سفر', cat: 'accessories', label_en: 'luggage travel bag', badge: '-40%', badgeColor: 'bg-purple-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/bc3e1a2237e5ac75a51b3189a09b479491f2f4d02c4ba9477fb2917cc15b1fc6.png', label_ar: 'أدوات تصفيف شعر', cat: 'beauty', label_en: 'hair styling tools', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/6f4b360bb32566776a9b57d5aef588c733abb226b03142129c6ba6ec314e96c3.png', label_ar: 'سماعة بلوتوث', cat: 'electronics', label_en: 'bluetooth speaker', badge: 'NEW', badgeColor: 'bg-emerald-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/e2dc6c31bc453224a0d3b69967f7c8a8b5a81483f739024b50a3219b593e4bc1.png', label_ar: 'معطف نسائي', cat: 'fashion', label_en: 'women coat jacket', badge: 'SALE', badgeColor: 'bg-pink-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/f33d3b79af98584131ccfbdcb4780c074a9d618186657739e37f87128ae4a684.png', label_ar: 'مصباح مكتب LED', cat: 'home', label_en: 'LED desk lamp', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/25debf1b4905589a5ec092feaf8c0e8aaf9bae22c6db327e613e693435c7f0c9.png', label_ar: 'فرش مكياج', cat: 'beauty', label_en: 'makeup brushes beauty', badge: '-25%', badgeColor: 'bg-pink-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/004f9d3a1c0ab1c85909541352cb40752e2284d80d14b7717668631ff9973131.png', label_ar: 'حذاء رسمي رجالي', cat: 'fashion', label_en: 'mens dress shoes formal', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/9f1aed96d4036fa0749d81f897ca7e2ec801029ce089475dc6b31ba48fe6c22a.png', label_ar: 'تابلت ذكي', cat: 'phones', label_en: 'tablet electronics', badge: 'HOT', badgeColor: 'bg-orange-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/00faad6b18225fabb6e939d65145c15acdd622543f2a1bf7b81bfeefb52cf6dc.png', label_ar: 'عطر فاخر', cat: 'beauty', label_en: 'luxury perfume fragrance', badge: 'VIP', badgeColor: 'bg-[#EAB308]' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/a748b9b8c4a078332c61ec4c27a62d73d084a35c41dfb4b924f6fd97e1ef4aea.png', label_ar: 'معدات تخييم', cat: 'garden', label_en: 'camping outdoor gear', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/1f833dad3f61c5dc3fd1dd782479efd608ad4e9f76164c240322120dda9cc608.png', label_ar: 'فرشاة أسنان كهربائية', cat: 'beauty', label_en: 'electric toothbrush health', badge: '-15%', badgeColor: 'bg-blue-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/e96cf8a02c731ad1eac9799c4eb23b5d0bb10969ac6c30cab9103f99885daee3.png', label_ar: 'حقيبة مدرسة أطفال', cat: 'kids', label_en: 'kids school backpack', badge: 'SALE', badgeColor: 'bg-green-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/c2e0c1adefff848eaeefbd440ad4278cbcce121c6454fe7cb70a74126308e9b9.png', label_ar: 'إضاءة سيارة LED', cat: 'automotive', label_en: 'car LED lights accessories', badge: 'NEW', badgeColor: 'bg-cyan-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/f34f1449ad8a330f54049a2a562f417b1388150a063be36dae61548e4405da9d.png', label_ar: 'ملابس رياضية نسائية', cat: 'sports', label_en: 'women activewear sports', badge: 'TREND', badgeColor: 'bg-purple-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/54076bb63aa9ba336a01079dac499c7571c973ff9ccd48b125f753eb4acac9c6.png', label_ar: 'باور بانك', cat: 'electronics', label_en: 'power bank charger', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/c955b6ff29c0f767bdf37bda9907c0e4a8af17fbd4394b18bcad814b1588627f.png', label_ar: 'حذاء باليه نسائي', cat: 'fashion', label_en: 'women ballet shoes', badge: '-35%', badgeColor: 'bg-red-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/fdb247298bc86c9fcca3ed60556989fed522b47abebe67a0c7cf48e1bf50a0ef.png', label_ar: 'زجاجة ماء حرارية', cat: 'home', label_en: 'thermos water bottle', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/5df17242f664a488e89ad7d47dbe458db36b08ec1549c34c6340575995c80620.png', label_ar: 'قبعات رجالية', cat: 'accessories', label_en: 'mens caps hats', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/0e928f61ae06e1d0128170e5eaaae74d979e99e52c7894c4b60d2303e3609c0e.png', label_ar: 'مكنسة روبوت ذكية', cat: 'home', label_en: 'robot vacuum smart home', badge: 'HOT', badgeColor: 'bg-orange-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/8c12029829be7d35d2355b89cc84b29772b4f8c842233fdf7664622f50ed0705.png', label_ar: 'حقيبة سهرة ذهبية', cat: 'accessories', label_en: 'gold clutch evening bag', badge: 'VIP', badgeColor: 'bg-[#EAB308]' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/d92d1d2f7a777d6bd3cc1099a34e50e3031885c81eb99abd808c113be0374ffa.png', label_ar: 'معدات دراجة', cat: 'sports', label_en: 'cycling helmet gear', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/bb3444c53d8002446fbe864aef20b4290b1b54b595a1bd18f0ec537c9489890f.png', label_ar: 'ناشر عطر منزلي', cat: 'home', label_en: 'oil diffuser aromatherapy', badge: '-20%', badgeColor: 'bg-emerald-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/2e40faa40bedb79f515f383400751ef0e498d7b9030db640829046cfcdfc03af.png', label_ar: 'ماكينة إسبرسو', cat: 'home', label_en: 'espresso coffee machine', badge: 'BEST', badgeColor: 'bg-amber-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/939b0e733be632f8b330c9c204653c01869a6e43f70c86ed84350eb2f1fd3f4b.png', label_ar: 'طقم ربطة عنق', cat: 'fashion', label_en: 'mens tie cufflinks set', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/625b9fde76f0ef6b6b59011fefaa26d4ca6b2f73fb0b9df96eb740c09c80b536.png', label_ar: 'سكوتر أطفال', cat: 'kids', label_en: 'kids scooter outdoor', badge: 'SALE', badgeColor: 'bg-blue-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/be6cfc1df69f939c57d13e48d24a5429e3e4989b01029875983e7533d897ea31.png', label_ar: 'بوتات نسائية', cat: 'fashion', label_en: 'women ankle boots', badge: 'NEW', badgeColor: 'bg-pink-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/782517201cc58d8ed78bc391a3ccbb26fd7b25db4bec3bee1191de1928780a0e.png', label_ar: 'طائرة درون', cat: 'electronics', label_en: 'camera drone tech', badge: 'HOT', badgeColor: 'bg-red-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/be08a1f466ddfeecbe788504d7b369d908abe95188b44acdfda79c47603d044b.png', label_ar: 'شجرة قطط', cat: 'pets', label_en: 'cat tree pet furniture', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/cb042e5d24711b2064eedfbc7fa361109adf770e82e88ec329bec80361425418.png', label_ar: 'غلاية كهربائية', cat: 'home', label_en: 'electric kettle kitchen', badge: '-15%', badgeColor: 'bg-orange-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/e88bb6b13304c338231f24799d5f290e118f452f65a81bcdf64ebc48030593fd.png', label_ar: 'طلاء أظافر', cat: 'beauty', label_en: 'nail polish beauty', badge: 'TREND', badgeColor: 'bg-purple-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/af86e9f4bb9e6bde7b9933182d9eb6accdc20d0a7c8c599fd38e8df7c1421f7d.png', label_ar: 'كيبورد وماوس', cat: 'electronics', label_en: 'keyboard mouse office', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/17fc9fb46b9d51f129262df78b816d7903ae1130e5a00e6c81da12e940599241.png', label_ar: 'مظلة مطر', cat: 'accessories', label_en: 'umbrella rain accessories', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/e64d5665c20c0c251e6f101849c5ffc45061f43cc57c33d83096b359e7034dcf.png', label_ar: 'وسائد ديكور', cat: 'home', label_en: 'throw pillows home decor', badge: '-30%', badgeColor: 'bg-emerald-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/af70d48f0af576550f8053795b88767e54238f52b820e1034d4cf487f90c3438.png', label_ar: 'طقم حلاقة رجالي', cat: 'beauty', label_en: 'mens grooming kit', badge: 'BEST', badgeColor: 'bg-amber-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/aa14466832722ade5876db26c9f85995b4fa59a363d914552cf1560968402404.png', label_ar: 'كاميرا أكشن', cat: 'electronics', label_en: 'action camera adventure', badge: 'HOT', badgeColor: 'bg-red-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/5962fe7fc40c3fef1487b093a4c5814447bc062fc0df8d39044c11620e2744ec.png', label_ar: 'حقيبة هاتف نسائية', cat: 'accessories', label_en: 'women phone crossbody bag', badge: 'NEW', badgeColor: 'bg-pink-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/e8c9e515c4133f55e5474301b939c9d5572035035ad7cab7e481e0004884b818.png', label_ar: 'شطرنج خشبي', cat: 'gaming', label_en: 'wooden chess board game', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/536bdc31f83d54164d7c9377384e2253cdf386f1652ede61ec89aa2ed4b55a3b.png', label_ar: 'أغطية مقاعد سيارة', cat: 'automotive', label_en: 'car seat covers auto', badge: '-25%', badgeColor: 'bg-purple-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/38f5794cd9c8cf808a2e919d8f95e4a3678e1abf8b6087b7d1495392ed33f85b.png', label_ar: 'مصباح قراءة', cat: 'home', label_en: 'reading lamp clip light', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/51f62c2706c7acb59a9db9ab2d903df2c9789e4cbd7ea94dc570ea6ed549600b.png', label_ar: 'صندل ويدج نسائي', cat: 'fashion', label_en: 'women wedge sandals', badge: 'SALE', badgeColor: 'bg-amber-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/17d5dca7883877e2c75b075b6eedf247b2e80a69db9141ec329be1831f7405ca.png', label_ar: 'ميزان ذكي', cat: 'electronics', label_en: 'smart scale health', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/a92616de2c244062b15360bdc2701e85002e0a475ca695c765f81c4440293be7.png', label_ar: 'رضّاعات أطفال', cat: 'kids', label_en: 'baby feeding bottles', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/2b59ee146d734b6a8528087942dd02418c9ba426d5411760270b4b361016812f.png', label_ar: 'إسوارة ذهب وردي', cat: 'jewelry', label_en: 'rose gold bracelet jewelry', badge: 'VIP', badgeColor: 'bg-[#EAB308]' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/030c180125759997fda741bbce7102e7e21a76a38ddfe520bc4c531f0c2026b3.png', label_ar: 'قلاية هوائية', cat: 'home', label_en: 'air fryer kitchen', badge: 'BEST', badgeColor: 'bg-orange-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/40f0c63395a753d5acac11274654bdc6506b5afcfc338b7b63a6296fc12a6c67.png', label_ar: 'حذاء لوفر رجالي', cat: 'fashion', label_en: 'mens loafer shoes', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/34d4223d1b171784913f9cc2483e560dc3bfd217c91e85299e74eee3351c3aad.png', label_ar: 'إضاءة حديقة شمسية', cat: 'garden', label_en: 'solar garden lights', badge: 'NEW', badgeColor: 'bg-green-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/169b5d00d30456688802c41ca4b0bbf7716b22fc63dcebfa94faa283718e761d.png', label_ar: 'شاحن لاسلكي', cat: 'electronics', label_en: 'wireless charger tech', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/2ac580323ed66acf9bfe2c808701d0b31c86bc5388cefa5d2dea4c610069910a.png', label_ar: 'قبعة شاطئ نسائية', cat: 'fashion', label_en: 'women beach hat summer', badge: '-40%', badgeColor: 'bg-pink-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/6dce804d08bdf9235689c26a794cda99b44f43b5f449a16e7f0f4b8f2ccff9b0.png', label_ar: 'حزام كلب ملون', cat: 'pets', label_en: 'dog harness leash pet', badge: null, badgeColor: '' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/2b195ea3349e8667034b5a645823af552d8eb8f82c272b983b9415f756f24054.png', label_ar: 'ساعة رياضية رقمية', cat: 'sports', label_en: 'digital sport watch', badge: 'HOT', badgeColor: 'bg-blue-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/209bd12826c24cf2dfaeb9881db21160d7588c33f37420319243c5d7b6d9d4b0.png', label_ar: 'حقيبة لابتوب', cat: 'electronics', label_en: 'laptop backpack bag', badge: 'TREND', badgeColor: 'bg-cyan-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/03ce409ca77b7187af986f3ee841994a5ff3f352214d255225da9a3be7162b11.png', label_ar: 'ماسك وجه كوري', cat: 'beauty', label_en: 'korean face mask skincare', badge: '-20%', badgeColor: 'bg-emerald-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/05fbe254541dd70d1a796fa58523d23309424ee6bffbda24f6bcdd32e1121f3e.png', label_ar: 'كاميرا مراقبة ذكية', cat: 'electronics', label_en: 'security camera smart home', badge: 'NEW', badgeColor: 'bg-red-500' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/0b7dbd32-4aa0-49c4-90a2-8e3b5ddac06c/images/1d9393c0567a30ea0947116eb73c6b0ade13d297cbdf7b808ff18137bd05edc6.png', label_ar: 'جاكيت جينز رجالي', cat: 'fashion', label_en: 'mens denim jacket', badge: null, badgeColor: '' },
];
// All warehouse images for showcase strip
const SHOWCASE_STRIP = [
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/e778416724cfbda3938e06f767933c3d23504e208149278111e65610b1203ef0.png', label_ar: 'المستودع الرئيسي' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/885db3397a890d864316b5822a4fa9516f975acf55bfb75635cf026863e3a00e.png', label_ar: 'صناديق S&S' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/65bf6a4b2b1472a2be287268aa44ed89d79c81cf4f919bb4c1c7e8e94d02e983.png', label_ar: 'فحص الجودة' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/dd3e375067bb2fe8e72dd5ff9e6f0bc4e6a4ae5ff6329aec583bb904b50a40d5.png', label_ar: 'محطة التغليف' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/f394b40d5c5a1ffff20853836878fdb84e55be6e452ad9e1031174dce5cc4156.png', label_ar: 'قسم الأزياء' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/0fdcca961465eb867a0022c9fad44454e5916579cf6bf24289ecb0200cf7a8ea.png', label_ar: 'الإلكترونيات' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/7964b774f9c737f8d80efd355cdb12051ec722e78d899d2a7cbb41dff4026277.png', label_ar: 'تجهيز الطلبات' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/c80635a4df29c6a2aa135c2051b2c997577ecd81a0fc8b4f5cf265935a0a26d1.png', label_ar: 'شاحنات الشحن' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/f03e29130f41fdac7337fa2619c99251f45b9ca3f4d5b1923eaa7d1319a61b0b.png', label_ar: 'قسم المنزل' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/6f62588034cba44e4355ad4d4b9eb41e4dc4e207570b27b80b4d4e23fee60be9.png', label_ar: 'الإكسسوارات' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/d9acc8928dcf246efc667175758dfd6fdf7bf7e9e8e5811695eef1632e217890.png', label_ar: 'نظام الباركود' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/be675aa3fa6a1f490621344552dd96482e7a9d938c014e412ba7a93173b6f6c3.png', label_ar: 'الرافعات الشوكية' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/2ecb0eb37526c681dddf05cdd954863084750b7b26899c5795df7182442d7ca4.png', label_ar: 'الورديه الليلية' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/d9320020ac329fbc22aff1ebb3b268a24b3898d46b816e37d1dade3c66f247e7.png', label_ar: 'خدمة العملاء' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/2189ac31a5abb9a9b53595a957b4fec42d7bfc068cbf3a4ce5221ed5847cfc61.png', label_ar: 'غرفة المراقبة' },
  { img: 'https://static.prod-images.emergentagent.com/jobs/4d3e2f11-23c3-432a-8f0a-cf1c911f4c15/images/5172386e62bdb1fafa660bb53ff467c09c344617bc1b88e5634455b428b3c2d1.png', label_ar: 'منظر جوي للمستودع' },
];

export default function LandingPage() {
  const { t, isRTL, lang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [marketplaces, setMarketplaces] = useState([]);
  const [successStories, setSuccessStories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [dailyDeals, setDailyDeals] = useState([]);
  const [homeSections, setHomeSections] = useState([]);
  const [productPopup, setProductPopup] = useState(null);
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem('ss_cart') || '[]'); } catch { return []; } });

  useEffect(() => {
    fetch(`${API_URL}/api/marketplaces`)
      .then(r => r.json())
      .then(data => setMarketplaces(data))
      .catch(console.error);
    fetch(`${API_URL}/api/success-stories`)
      .then(r => r.json())
      .then(data => setSuccessStories(data))
      .catch(console.error);
    fetch(`${API_URL}/api/products?limit=80`)
      .then(r => r.json())
      .then(data => setFeaturedProducts(data.products || []))
      .catch(console.error);
    fetch(`${API_URL}/api/daily-deals`).then(r=>r.json()).then(d=>setDailyDeals(d.categories||[])).catch(console.error);
    fetch(`${API_URL}/api/homepage-sections`).then(r=>r.json()).then(d=>setHomeSections(d.sections||[])).catch(console.error);
  }, []);

  const addToCart = (product) => {
    const existing = cart.find(c => c.id === product.id);
    let newCart;
    if (existing) { newCart = cart.map(c => c.id === product.id ? { ...c, qty: c.qty + 1 } : c); }
    else { newCart = [...cart, { ...product, qty: 1 }]; }
    setCart(newCart);
    localStorage.setItem('ss_cart', JSON.stringify(newCart));
  };
  const cartTotal = cart.reduce((sum, c) => sum + (c.sell_price || 0) * c.qty, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.qty, 0);
  const [cartBounce, setCartBounce] = useState(false);
  const addToCartAnimated = (product) => {
    addToCart(product);
    localStorage.setItem('ss_cart_bounce', Date.now().toString());
    window.dispatchEvent(new Event('storage'));
    toast.success(isRTL ? 'تمت الإضافة للسلة!' : 'Added to cart!', {
      description: isRTL ? (product.name_ar || product.label_ar || '') : (product.name || product.label_en || ''),
      duration: 2000,
    });
  };

  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  // Hero image slider - 8 seconds per slide for better readability
  const [heroSlide, setHeroSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHeroSlide(p => (p + 1) % PREMIUM_ADS.length), 8000);
    return () => clearInterval(t);
  }, []);

  // Warehouse image popup
  const [warehousePopup, setWarehousePopup] = useState(null);

  // Tasks animation step
  const [taskStep, setTaskStep] = useState(0);
  const taskSteps = [
    { icon: Target, title: isRTL ? 'نفّذ المهام اليومية' : 'Daily Tasks', desc: isRTL ? 'شارك منتجات، ادعُ أصدقاء، حقق أهداف المبيعات' : 'Share products, invite friends, hit sales goals', color: '#14b8a6' },
    { icon: TrendingUp, title: isRTL ? 'ارتقِ بمستواك' : 'Level Up', desc: isRTL ? 'كلّما نفّذت مهام أكثر، ترتقي لمستوى أعلى بعمولة أكبر' : 'More tasks = higher tier = bigger commission', color: '#EAB308' },
    { icon: Trophy, title: isRTL ? 'عمولات تصاعدية' : 'Growing Commissions', desc: isRTL ? 'المستوى البرونزي 15% → الفضي 20% → الذهبي 25% → الماسي 30%' : 'Bronze 15% → Silver 20% → Gold 25% → Diamond 30%', color: '#a855f7' },
    { icon: Gift, title: isRTL ? 'مكافآت وبونصات' : 'Rewards & Bonuses', desc: isRTL ? 'احصل على عملات إضافية ومكافآت حصرية عند إكمال التحديات' : 'Extra coins & exclusive rewards for completing challenges', color: '#ec4899' },
  ];
  useEffect(() => {
    const t = setInterval(() => setTaskStep(p => (p + 1) % taskSteps.length), 3500);
    return () => clearInterval(t);
  }, []);

  const steps = [
    { icon: Store, title: isRTL ? 'اختر المنتجات' : 'Pick Products', desc: isRTL ? 'تصفح أكثر من 2000 منتج وأضفها لمتجرك' : 'Browse 2000+ products and add to your store', color: 'text-primary' },
    { icon: ShoppingCart, title: isRTL ? 'شارك رابطك' : 'Share Your Link', desc: isRTL ? 'شارك على واتساب، إنستغرام، تيك توك' : 'Share on WhatsApp, Instagram, TikTok', color: 'text-accent' },
    { icon: Trophy, title: isRTL ? 'العميل يشتري' : 'Customer Buys', desc: isRTL ? 'عندما يشتري عميل من رابطك، نشحن من مستودعاتنا' : 'When a customer buys, we ship from our warehouse', color: 'text-[hsl(var(--success))]' },
    { icon: TrendingUp, title: isRTL ? 'اكسب عمولتك' : 'Earn Commission', desc: isRTL ? 'تحصل على عمولة فوراً - بدون تخزين أو شحن' : 'Get your commission instantly - no storage or shipping', color: 'text-[hsl(var(--info))]' },
  ];

  const stats = [
    { label: isRTL ? 'مدفوعات للبائعين' : 'Paid to Sellers', value: '$3.8M' },
    { label: isRTL ? 'طلب مكتمل' : 'Orders Completed', value: '120,000+' },
    { label: isRTL ? 'دولة حول العالم' : 'Countries', value: '32' },
    { label: isRTL ? 'مستودع شحن' : 'Warehouses', value: '8' },
  ];

  return (
    <div className="pb-16 overflow-x-hidden" data-testid="landing-page">
      {/* ===== SEAMLESS PREMIUM ADVERTISEMENT BANNER ===== */}
      <section className="relative overflow-hidden" data-testid="mega-hero" style={{ marginTop: '-1rem' }}>
        
        {/* ── DESKTOP: Full-Width Seamless Banner ── */}
        <div className="hidden lg:block relative group" style={{ height: '560px' }}>
          <AnimatePresence mode="wait">
            {(() => {
              const ad = PREMIUM_ADS[heroSlide % PREMIUM_ADS.length];
              return (
                <motion.div 
                  key={ad.id} 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  exit={{ opacity: 0 }} 
                  transition={{ duration: 1 }}
                  className="absolute inset-0"
                >
                  {/* Full Background Image - Positioned to show on opposite side of text */}
                  <div className="absolute inset-0">
                    <motion.img 
                      src={ad.image} 
                      alt=""
                      initial={{ scale: 1.05 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 12, ease: "linear" }}
                      className="w-full h-full object-cover"
                      style={{ 
                        objectPosition: isRTL ? '35% center' : '65% center'
                      }}
                    />
                    
                    {/* Gradient: WHITE area on text side, IMAGE visible on opposite side */}
                    <div 
                      className="absolute inset-0" 
                      style={{
                        background: isRTL 
                          ? 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 28%, rgba(255,255,255,0.92) 35%, rgba(255,255,255,0.7) 42%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.1) 58%, transparent 68%)'
                          : 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 28%, rgba(255,255,255,0.92) 35%, rgba(255,255,255,0.7) 42%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.1) 58%, transparent 68%)'
                      }}
                    />
                    
                    {/* Top fade for seamless header blend */}
                    <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-white/30 to-transparent" />
                    
                    {/* Bottom fade for seamless section blend */}
                    <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/70 to-transparent" />
                  </div>
                  
                  {/* Content Container - Text on LEFT for LTR, RIGHT for RTL */}
                  <div 
                    className="absolute inset-y-0 w-[40%] flex items-center"
                    style={{
                      left: isRTL ? 'auto' : '0',
                      right: isRTL ? '0' : 'auto',
                      paddingLeft: isRTL ? '0' : '32px',
                      paddingRight: isRTL ? '32px' : '0',
                    }}
                  >
                    <div 
                      className="w-full py-4"
                      style={{ 
                        textAlign: isRTL ? 'right' : 'left',
                      }}
                    >
                      
                      {/* S&S Brand Tag - In Arabic: Text on LEFT, Icon on RIGHT to align with badge below */}
                      <motion.div 
                        initial={{ opacity: 0, y: -15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center gap-3 mb-4"
                        style={{ 
                          direction: 'ltr',
                          flexDirection: 'row',
                          justifyContent: isRTL ? 'flex-end' : 'flex-start' 
                        }}
                      >
                        {/* For RTL: Text first (appears on left), Icon second (appears on right, aligned with badge) */}
                        {isRTL ? (
                          <>
                            <div style={{ textAlign: 'right' }}>
                              <span className="block text-xs font-bold text-gray-500 tracking-widest">SHOP & SHIP</span>
                              <span className="block text-[10px] text-gray-400">✓ جودة مضمونة</span>
                            </div>
                            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-emerald-400 flex items-center justify-center shadow-lg shadow-primary/30">
                              <span className="text-xs font-black text-white">S&S</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-emerald-400 flex items-center justify-center shadow-lg shadow-primary/30">
                              <span className="text-xs font-black text-white">S&S</span>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <span className="block text-xs font-bold text-gray-500 tracking-widest">SHOP & SHIP</span>
                              <span className="block text-[10px] text-gray-400">✓ Quality Guaranteed</span>
                            </div>
                          </>
                        )}
                      </motion.div>
                      
                      {/* Category Badge */}
                      <motion.div
                        initial={{ x: isRTL ? 30 : -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="mb-3"
                      >
                        <span 
                          className={`inline-flex items-center gap-2 ${ad.badgeColor} text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg`}
                          style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}
                        >
                          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          {ad.badge}
                        </span>
                      </motion.div>
                      
                      {/* Main Headline */}
                      <motion.h1
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="text-4xl xl:text-5xl 2xl:text-6xl font-black text-gray-900 mb-2 leading-[1.1]"
                        style={{ 
                          textShadow: '0 2px 20px rgba(0,0,0,0.05)',
                          textAlign: isRTL ? 'right' : 'left',
                        }}
                      >
                        {isRTL ? ad.headline_ar : ad.headline_en}
                      </motion.h1>
                      
                      {/* Subheadline */}
                      <motion.h2
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.6 }}
                        className="text-xl xl:text-2xl font-bold text-gray-600 mb-3"
                        style={{ textAlign: isRTL ? 'right' : 'left' }}
                      >
                        {isRTL ? ad.subheadline_ar : ad.subheadline_en}
                      </motion.h2>
                      
                      {/* Description */}
                      <motion.p
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                        className="text-base text-gray-500 mb-5 leading-relaxed"
                        style={{ textAlign: isRTL ? 'right' : 'left' }}
                      >
                        {isRTL ? ad.description_ar : ad.description_en}
                      </motion.p>
                      
                      {/* CTA Button - Same structure as badge for consistent alignment */}
                      <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                        className="mb-5"
                      >
                        <motion.button
                          whileHover={{ scale: 1.05, y: -3 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => navigate(`/products?category=${ad.category}`)}
                          className="inline-flex relative overflow-hidden bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white text-base font-bold px-8 py-4 rounded-xl shadow-xl shadow-gray-900/30 transition-all"
                          data-testid={`ad-cta-${ad.id}`}
                        >
                          <span 
                            className="relative z-10 flex items-center gap-2"
                            style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}
                          >
                            {isRTL ? ad.cta_ar : ad.cta_en}
                            <motion.span
                              animate={{ x: isRTL ? [0, -6, 0] : [0, 6, 0] }}
                              transition={{ repeat: Infinity, duration: 1.2 }}
                              className="text-lg"
                            >
                              {isRTL ? '←' : '→'}
                            </motion.span>
                          </span>
                          <motion.span 
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ repeat: Infinity, duration: 2.5, ease: 'linear' }}
                            className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" 
                          />
                        </motion.button>
                      </motion.div>
                      
                      {/* Trust indicators */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                        className="flex items-center gap-4 mt-6"
                        style={{ 
                          flexDirection: isRTL ? 'row-reverse' : 'row',
                          justifyContent: isRTL ? 'flex-end' : 'flex-start' 
                        }}
                      >
                        {[
                          { icon: Truck, text: isRTL ? 'شحن مجاني' : 'Free Shipping', color: 'text-emerald-600 bg-emerald-50' },
                          { icon: Shield, text: isRTL ? 'ضمان' : 'Guaranteed', color: 'text-blue-600 bg-blue-50' },
                          { icon: Star, text: isRTL ? '+5K' : '+5K', color: 'text-amber-600 bg-amber-50' },
                        ].map((item, i) => (
                          <div 
                            key={i} 
                            className="flex items-center gap-1.5"
                            style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}
                          >
                            <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center`}>
                              <item.icon className="w-4 h-4" />
                            </div>
                            <span className="text-xs font-medium text-gray-600">{item.text}</span>
                          </div>
                        ))}
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
          
          {/* Navigation Arrows - Visible on hover, positioned at extreme edges */}
          <button 
            onClick={(e) => { e.stopPropagation(); setHeroSlide((heroSlide - 1 + PREMIUM_ADS.length) % PREMIUM_ADS.length); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg backdrop-blur-sm flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100 z-20 hover:scale-110"
            data-testid="banner-prev"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); setHeroSlide((heroSlide + 1) % PREMIUM_ADS.length); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg backdrop-blur-sm flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100 z-20 hover:scale-110"
            data-testid="banner-next"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
          
          {/* Slide Counter - Minimal */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
            <div className="flex items-center gap-2 bg-black/20 backdrop-blur-md rounded-full px-4 py-2">
              <span className="text-white/90 text-sm font-bold">{heroSlide + 1}</span>
              <span className="text-white/50 text-sm">/</span>
              <span className="text-white/50 text-sm">{PREMIUM_ADS.length}</span>
            </div>
          </div>
        </div>

        {/* ── MOBILE/TABLET: V17 EXACT Original Design ── */}
        <div className="lg:hidden hero-gradient relative overflow-hidden rounded-2xl bg-card" data-testid="mega-hero-mobile">
          {/* Ambient glows */}
          <div className="absolute top-10 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 left-10 w-56 h-56 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-primary/3 rounded-full blur-[120px] pointer-events-none" />

          {/* Floating S&S logos + product icons */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
            {[
              { x: '4%', y: '6%', s: 44, d: 14, del: 0, r: -12 },
              { x: '90%', y: '10%', s: 28, d: 16, del: 2, r: 10 },
              { x: '78%', y: '58%', s: 24, d: 13, del: 4, r: -18 },
              { x: '22%', y: '38%', s: 22, d: 17, del: 6, r: 14 },
            ].map((f, i) => (
              <div key={`f-${i}`} className="absolute opacity-[0.12]"
                style={{ left: f.x, top: f.y }}>
                <div className="rounded-lg border border-primary/10 bg-primary/[0.03] flex items-center justify-center"
                  style={{ width: f.s, height: f.s * 0.85 }}>
                  <span className="font-bold text-primary text-center leading-none" style={{ fontSize: Math.max(7, f.s * 0.22) }}>S&S</span>
                </div>
              </div>
            ))}
          </div>

          <div className="relative z-10">
            {/* ── 1. FULL-WIDTH HERO BANNER SLIDER ── */}
            <div className="relative overflow-hidden" style={{ minHeight: '180px' }}>
              <AnimatePresence mode="wait">
                <motion.div key={heroSlide} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
                  className="relative w-full aspect-[2.2/1] sm:aspect-[3/1] overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/products?category=${HERO_SLIDER[heroSlide].cat || 'accessories'}`)}>
                  <img src={HERO_SLIDER[heroSlide].img} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10" />
                  {HERO_SLIDER[heroSlide].badge && (
                    <span className={`absolute top-3 left-3 ${HERO_SLIDER[heroSlide].badgeColor} text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg`}>{HERO_SLIDER[heroSlide].badge}</span>
                  )}
                  {/* S&S Logo - top right corner */}
                  <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-md rounded-xl px-2.5 py-1.5 border border-white/15 flex items-center gap-1.5 shadow-lg">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-primary to-emerald-400 flex items-center justify-center">
                      <span className="text-[7px] font-black text-white">S&S</span>
                    </div>
                    <span className="text-[10px] font-bold text-white/90 hidden sm:block">SHOP & SHIP</span>
                  </div>
                  <div className="absolute bottom-3 right-3 text-right">
                    <p className="text-white text-sm sm:text-base font-bold drop-shadow-lg">{HERO_SLIDER[heroSlide].label_ar}</p>
                  </div>
                </motion.div>
              </AnimatePresence>
              {/* Dots */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {HERO_SLIDER.slice(0, 12).map((_, i) => (
                  <button key={i} onClick={() => setHeroSlide(i)}
                    className={`h-1 rounded-full transition-all duration-300 ${heroSlide === i ? 'w-4 bg-primary' : 'w-1.5 bg-white/30'}`} />
                ))}
              </div>
            </div>

            {/* ── 2. TRUST BAR - V17 Enhanced - Mobile Optimized ── */}
            <div className="bg-gradient-to-r from-primary/5 via-emerald-50/50 to-primary/5 border-y border-primary/10 py-4 px-2 overflow-hidden">
              <div className="flex items-center justify-between gap-1 sm:gap-6 sm:justify-center max-w-full">
                {[
                  { icon: Users, val: '+50K', lbl: isRTL ? 'بائع نشط' : 'Sellers', color: 'text-blue-600' },
                  { icon: Truck, val: isRTL ? 'مجاني' : 'FREE', lbl: isRTL ? 'شحن' : 'Shipping', color: 'text-emerald-600' },
                  { icon: Globe, val: '32', lbl: isRTL ? 'دولة' : 'Countries', color: 'text-purple-600' },
                  { icon: Shield, val: '100%', lbl: isRTL ? 'ضمان' : 'Guarantee', color: 'text-amber-600' },
                ].map((t, i) => (
                  <div 
                    key={i} 
                    className="flex flex-col items-center text-center flex-1 min-w-0 sm:flex-row sm:gap-2.5 sm:flex-initial"
                  >
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center ${t.color} mb-1 sm:mb-0`}>
                      <t.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="flex flex-col items-center sm:items-start">
                      <span className={`text-sm sm:text-xl font-black ${t.color} leading-tight`}>{t.val}</span>
                      <span className="text-[9px] sm:text-xs text-gray-500 font-medium leading-tight">{t.lbl}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── 3. HERO TEXT ── */}
            <div className="px-5 sm:px-10 lg:px-14 pt-6 sm:pt-10 pb-5">
              <div className="max-w-3xl">
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3 leading-[1.2]">
                  <span className="text-foreground">{isRTL ? 'بِع منتجات واكسب ' : 'Sell Products & Earn '}</span>
                  <span className="bg-gradient-to-r from-[hsl(var(--ss-gold))] to-amber-400 bg-clip-text text-transparent">{isRTL ? 'عمولة 20%' : '20% Commission'}</span>
                  <span className="text-foreground">{isRTL ? ' بدون مخزون!' : ' Without Inventory!'}</span>
                </h1>
                <p className="text-base sm:text-lg text-muted-foreground mb-6 leading-relaxed max-w-lg">
                  {isRTL ? 'اختر المنتجات من كتالوجنا، شارك رابط متجرك — نحن نخزّن، نغلّف، ونوصّل. أنت فقط تبيع وتكسب العمولة!' : 'Pick from our catalog, share your store link — we store, pack, and deliver. You just sell and earn!'}
                </p>
                <div className="flex flex-row gap-3">
                  <div className="rounded-2xl flex-1 hover:scale-105 hover:-translate-y-1 active:scale-95 transition-transform duration-200">
                    <Button size="lg" onClick={() => navigate(isAuthenticated ? '/marketplaces' : '/register')}
                      className="relative w-full h-14 px-4 sm:px-8 text-sm sm:text-base font-bold rounded-2xl bg-gradient-to-r from-[hsl(var(--ss-gold))] to-[hsl(var(--ss-gold-2))] text-black shadow-[0_8px_30px_rgba(234,179,8,0.35)] overflow-hidden group/btn gap-2 border-2 border-yellow-300/40 animate-pulse-glow"
                      data-testid="hero-cta-button">
                      <TrendingUp className="w-5 h-5" />
                      <span className="relative z-10">{isRTL ? 'ابدأ التجارة' : 'Start Trading'}</span>
                    </Button>
                  </div>
                  <div className="rounded-2xl flex-1 hover:scale-105 hover:-translate-y-1 active:scale-95 transition-transform duration-200">
                    <Button size="lg" variant="outline" onClick={() => navigate('/tutorials')}
                      className="w-full h-14 px-4 sm:px-8 text-sm sm:text-base font-bold rounded-2xl border-2 border-primary/50 text-primary hover:bg-primary/10 hover:border-primary/70 transition-all gap-2 shadow-[0_4px_20px_rgba(20,184,166,0.1)] hover:shadow-[0_0_25px_rgba(20,184,166,0.3)]"
                      data-testid="hero-learn-button">
                      {isRTL ? 'كيف تبدأ؟' : 'How?'}<Arrow className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── 4. QUICK CATEGORY ROW - ALL 20 CATEGORIES ── */}
            <div className="px-4 sm:px-10 pb-5">
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 sm:gap-3">
                {[
                  { icon: '👜', ar: 'اكسسوارات نسائية', cat: 'accessories' },
                  { icon: '👗', ar: 'أزياء نسائية', cat: 'fashion' },
                  { icon: '💄', ar: 'جمال وصحة', cat: 'beauty' },
                  { icon: '💎', ar: 'مجوهرات', cat: 'jewelry' },
                  { icon: '🕶️', ar: 'اكسسوارات رجالية', cat: 'men_accessories' },
                  { icon: '👔', ar: 'أزياء رجالية', cat: 'men_fashion' },
                  { icon: '🧴', ar: 'عناية رجالية', cat: 'men_grooming' },
                  { icon: '⌚', ar: 'ساعات رجالية', cat: 'men_watches' },
                  { icon: '📱', ar: 'إلكترونيات', cat: 'electronics' },
                  { icon: '🏠', ar: 'المنزل والمطبخ', cat: 'home' },
                  { icon: '⚽', ar: 'رياضة ولياقة', cat: 'sports' },
                  { icon: '🎮', ar: 'ألعاب', cat: 'toys' },
                  { icon: '📲', ar: 'هواتف وأجهزة', cat: 'phones' },
                  { icon: '💻', ar: 'حواسيب', cat: 'computers' },
                  { icon: '💪', ar: 'مكملات غذائية', cat: 'supplements' },
                  { icon: '🐾', ar: 'مستلزمات حيوانات', cat: 'pet' },
                  { icon: '👶', ar: 'أطفال ورضع', cat: 'baby' },
                  { icon: '🌿', ar: 'حديقة وخارجي', cat: 'garden' },
                  { icon: '🚗', ar: 'سيارات', cat: 'automotive' },
                  { icon: '📎', ar: 'مستلزمات مكتبية', cat: 'office' },
                ].map((c, i) => (
                  <button key={i}
                    onClick={() => navigate(`/products?category=${c.cat}`)}
                    className="flex flex-col items-center gap-1 hover:-translate-y-1 hover:scale-105 active:scale-95 transition-all duration-200">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-card border border-border/50 flex items-center justify-center text-base sm:text-lg shadow-sm hover:shadow-md hover:border-primary/30 transition-all">
                      {c.icon}
                    </div>
                    <span className="text-[8px] sm:text-[9px] text-muted-foreground font-medium text-center leading-tight line-clamp-2">{isRTL ? c.ar : c.cat}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* ── DYNAMIC PRODUCT CAROUSEL (70+ real products) ── */}
            <div className="pb-6">
              <div className="flex items-center gap-2 px-5 sm:px-10 mb-4">
                <Store className="w-3.5 h-3.5 text-primary" />
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">{isRTL ? 'منتجات مميزة وعروض حصرية' : 'Featured Products & Exclusive Deals'}</p>
                <span className="text-[9px] text-muted-foreground/50 font-mono">70+</span>
              </div>
              <div className="relative px-5 sm:px-10">
                <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide" id="hero-products-scroll-mobile">
                  {HERO_SLIDER.map((p, i) => (
                    <div key={i} className="flex-shrink-0 w-40 sm:w-48 cursor-pointer group/card hover:-translate-y-1 transition-transform duration-200"
                      onClick={() => navigate(`/products?category=${p.cat || 'accessories'}`)}>
                      <div className="relative rounded-xl overflow-hidden ring-1 ring-border/50 group-hover/card:ring-primary/40 transition-all shadow-sm group-hover/card:shadow-lg group-hover/card:shadow-primary/5">
                        <div className="aspect-square overflow-hidden bg-muted/10">
                          <img src={p.img} alt="" className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-500" loading="lazy" />
                        </div>
                        {p.badge && (
                          <span className={`absolute top-2 left-2 ${p.badgeColor} text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md shadow`}>{p.badge}</span>
                        )}
                        <div className="absolute top-2 right-2 w-7 h-7 rounded-md bg-gradient-to-br from-primary to-emerald-400 flex items-center justify-center shadow-md border border-white/20">
                          <span className="text-[7px] font-black text-white leading-none">S&S</span>
                        </div>
                      </div>
                      <div className="mt-1.5 px-0.5">
                        <p className="text-[11px] font-medium text-foreground truncate">{p.label_ar}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="absolute left-1 top-1/3 w-8 h-8 rounded-full bg-card/90 shadow-lg border border-border flex items-center justify-center opacity-50 hover:opacity-100 z-10"
                  onClick={() => document.getElementById('hero-products-scroll-mobile')?.scrollBy({ left: -350, behavior: 'smooth' })}><ChevronLeft className="w-4 h-4" /></button>
                <button className="absolute right-1 top-1/3 w-8 h-8 rounded-full bg-card/90 shadow-lg border border-border flex items-center justify-center opacity-50 hover:opacity-100 z-10"
                  onClick={() => document.getElementById('hero-products-scroll-mobile')?.scrollBy({ left: 350, behavior: 'smooth' })}><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>

            {/* ── DIVIDER ── */}
            <div className="h-px mx-5 sm:mx-10 bg-gradient-to-r from-transparent via-border to-transparent" />

            {/* ── WAREHOUSE STRIP ── */}
            <div className="py-6 overflow-hidden">
              <div className="px-5 sm:px-10 mb-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Warehouse className="w-4 h-4 text-primary" />
                  <h3 className="text-sm sm:text-base font-bold text-foreground">{isRTL ? 'من داخل مستودعاتنا' : 'Inside Our Warehouses'}</h3>
                </div>
                <p className="text-[11px] sm:text-xs text-muted-foreground mb-3">{isRTL ? 'مستودعات حقيقية في 5 دول — نخزّن ونغلّف ونشحن منتجاتك بأعلى معايير الجودة' : 'Real warehouses in 5 countries — storing, packing & shipping with top quality'}</p>
                <div className="flex gap-3">
                  {[
                    { val: '48', lbl: isRTL ? 'قسم' : 'Sections' },
                    { val: '8', lbl: isRTL ? 'مستودع' : 'Warehouses' },
                    { val: '5', lbl: isRTL ? 'دول' : 'Countries' },
                  ].map((s, i) => (
                    <div key={i} className="flex items-baseline gap-1">
                      <span className="text-sm font-bold text-primary font-mono">{s.val}</span>
                      <span className="text-[10px] text-muted-foreground">{s.lbl}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative" style={{ direction: 'ltr' }}>
                <div className="flex gap-3 showcase-scroll" style={{ width: 'max-content' }}>
                  {[...SHOWCASE_STRIP, ...SHOWCASE_STRIP].map((s, i) => (
                    <div key={i} className="relative flex-shrink-0 w-52 sm:w-60 h-40 sm:h-48 rounded-xl overflow-hidden ring-1 ring-border/50 group cursor-pointer hover:ring-primary/40 transition-all shadow-sm hover:shadow-lg"
                      onClick={() => setWarehousePopup(s)}>
                      <img src={s.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-md bg-gradient-to-br from-primary to-emerald-400 flex items-center justify-center shadow border border-white/20">
                        <span className="text-[6px] font-black text-white">S&S</span>
                      </div>
                      <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20">
                        <Zap className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-xs font-bold text-white drop-shadow-lg">{s.label_ar}</p>
                        <p className="text-[9px] text-white/50">SHOP & SHIP</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── DIVIDER ── */}
            <div className="h-px mx-5 sm:mx-10 bg-gradient-to-r from-transparent via-border to-transparent" />

            {/* ── HOW IT WORKS - DETAILED ── */}
            <div className="px-5 sm:px-10 lg:px-14 py-8">
              <div className="text-center mb-6">
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground mb-1">{isRTL ? 'كيف تصبح تاجراً من بيتك؟' : 'How to Become a Home Seller?'}</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">{isRTL ? 'بدون مخزون، بدون شحن، بدون خبرة سابقة — فقط 5 خطوات' : 'No inventory, no shipping, no experience — just 5 steps'}</p>
              </div>
              {/* Steps 1-4 grid */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                {[
                  { n: '01', icon: CreditCard, t: isRTL ? 'اشحن رصيدك' : 'Top Up', d: isRTL ? 'اشترِ عملات S&S بالـ USDT — طريقة دفع آمنة وفورية بدون وسطاء أو رسوم بنكية' : 'Buy S&S Coins with USDT — secure, instant, no bank fees', clr: '#EAB308' },
                  { n: '02', icon: Store, t: isRTL ? 'اختر منتجاتك' : 'Pick Products', d: isRTL ? 'تصفّح +2000 منتج في كتالوجنا واشترِ بعملات S&S — المنتج يُضاف لمتجرك الخاص تلقائياً' : 'Browse 2000+ products, buy with S&S Coins — auto-added to your store', clr: '#14b8a6' },
                  { n: '03', icon: Share2, t: isRTL ? 'سوّق وبِع' : 'Market & Sell', d: isRTL ? 'شارك رابط متجرك على فيسبوك، إنستغرام، تيك توك، واتساب — عندما يشتري أحد، أنت تكسب!' : 'Share on FB, IG, TikTok, WhatsApp — when someone buys, you earn!', clr: '#3b82f6' },
                  { n: '04', icon: Truck, t: isRTL ? 'نحن نتكفّل بالباقي' : 'We Handle the Rest', d: isRTL ? 'نخزّن المنتج في مستودعاتنا، نغلّفه باحترافية، ونشحنه مباشرة لباب المشتري' : 'We store, pack professionally & ship directly to the buyer', clr: '#a855f7' },
                ].map((step, i) => (
                  <div key={i}
                    className="rounded-xl p-4 border-2 relative overflow-hidden bg-gradient-to-b hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                    style={{ borderColor: `${step.clr}25`, background: `linear-gradient(to bottom, ${step.clr}08, transparent)` }}>
                    <span className="absolute -bottom-1 -left-1 text-[50px] font-black leading-none opacity-[0.04] font-mono">{step.n}</span>
                    <div className="relative">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${step.clr}15`, border: `1.5px solid ${step.clr}30` }}>
                          <step.icon className="w-4 h-4" style={{ color: step.clr }} />
                        </div>
                        <span className="text-[10px] font-mono font-bold opacity-20">{step.n}</span>
                      </div>
                      <h3 className="text-sm font-bold text-foreground mb-1">{step.t}</h3>
                      <p className="text-[10px] text-muted-foreground leading-relaxed">{step.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Step 5 - special golden */}
              <div
                className="rounded-xl p-4 border-2 border-[hsl(var(--ss-gold)/0.3)] relative overflow-hidden bg-gradient-to-r from-[hsl(var(--ss-gold)/0.08)] via-[hsl(var(--ss-gold)/0.04)] to-transparent ring-1 ring-[hsl(var(--ss-gold)/0.15)] hover:shadow-lg hover:shadow-[hsl(var(--ss-gold)/0.1)] transition-all">
                <span className="absolute -bottom-1 -left-1 text-[50px] font-black leading-none opacity-[0.04] font-mono">05</span>
                <div className="relative flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[hsl(var(--ss-gold)/0.15)] border-[1.5px] border-[hsl(var(--ss-gold)/0.3)]">
                    <TrendingUp className="w-5 h-5 text-[hsl(var(--ss-gold))]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-foreground">{isRTL ? 'اكسب واترقَّ!' : 'Earn & Level Up!'}</h3>
                    <p className="text-[10px] text-muted-foreground leading-relaxed">{isRTL ? 'تحصل على فارق الربح + عمولة إضافية على كل بيعة. كلّما بعت أكثر، ترتقي بمستواك وتزيد نسبة عمولتك — حقق أهداف المبيعات واكسب مكافآت حصرية!' : 'Get profit margin + bonus commission per sale. Sell more = higher tier = bigger commission. Hit sales goals for exclusive rewards!'}</p>
                  </div>
                  <span className="text-[10px] font-mono font-bold opacity-20">05</span>
                </div>
              </div>
            </div>

            {/* ── MERGED CTA SECTION ── */}
            <div className="px-5 sm:px-10 lg:px-14 py-8 sm:py-10">
              <div className="text-center mb-6">
                <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-foreground leading-tight mb-2">
                  {isRTL ? 'المجالات مفتوحة! ابدأ بالتجارة من منزلك' : 'Start trading from home'}
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground">{isRTL ? 'لا تحتاج مكتب ولا مخزون — فقط هاتفك وطموحك!' : "No office — just your phone!"}</p>
              </div>
              {/* Feature pills */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {[
                  { icon: Target, t: isRTL ? 'مهام يومية' : 'Tasks', d: isRTL ? 'نفّذ واكسب' : 'Do & earn', clr: '#14b8a6' },
                  { icon: Trophy, t: isRTL ? 'عمولات أعلى' : 'Tiers', d: isRTL ? 'ارتقِ بمستواك' : 'Level up', clr: '#EAB308' },
                  { icon: Gift, t: isRTL ? 'مكافآت' : 'Rewards', d: isRTL ? 'بونصات إضافية' : 'Extra bonuses', clr: '#a855f7' },
                ].map((task, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-card border border-border/50 rounded-xl px-4 py-3 shadow-sm hover:shadow-md hover:border-primary/30 transition-all">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${task.clr}15`, border: `1.5px solid ${task.clr}30` }}>
                      <task.icon className="w-4.5 h-4.5" style={{ color: task.clr }} />
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-foreground">{task.t}</p>
                      <p className="text-[9px] text-muted-foreground">{task.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* CTA Button */}
              <div className="flex flex-col items-center gap-3">
                <div className="rounded-2xl animate-pulse-glow animate-gentle-bounce hover:scale-105 active:scale-95 transition-transform duration-200">
                  <Button size="lg" onClick={() => navigate(isAuthenticated ? '/buy-coins' : '/register')}
                    className="relative h-14 sm:h-15 px-10 sm:px-12 text-base sm:text-lg font-bold rounded-2xl bg-gradient-to-r from-[hsl(var(--ss-gold))] to-[hsl(var(--ss-gold-2))] text-black shadow-xl gap-2.5 border-2 border-yellow-300/30 overflow-hidden group/cta"
                    data-testid="hero-buy-coins-button">
                    <span className="animate-smooth-spin inline-block">
                      <DollarSign className="w-5 h-5" />
                    </span>
                    {isRTL ? 'اشترِ عملاتك وابدأ!' : 'Buy Coins & Start!'}
                    <span className={isRTL ? 'animate-arrow-rtl' : 'animate-arrow-ltr'}>{isRTL ? '←' : '→'}</span>
                    <span className="absolute inset-0 -translate-x-full group-hover/cta:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">{isRTL ? 'انضم لأكثر من 50,000 بائع نشط' : 'Join 50,000+ active sellers'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== REST OF PAGE - Seamlessly Connected ===== */}
      <div className="space-y-8 px-4 sm:px-6 lg:px-8 -mt-8">

          {/* ── 3. HERO SECTION - Desktop Enhanced ── */}
          <div className="hidden lg:block px-10 lg:px-14 py-8">
            <div className="max-w-7xl mx-auto grid grid-cols-2 gap-12 items-center">
              {/* Left Side - Text Content */}
              <div className="space-y-6">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-emerald-100 rounded-full px-4 py-2 border border-primary/20">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-primary to-emerald-400 flex items-center justify-center animate-smooth-spin">
                    <Zap className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-primary">{isRTL ? 'أسهل طريقة للربح من الإنترنت' : 'Easiest way to earn online'}</span>
                </div>

                {/* Main Headline */}
                <div>
                  <h1 className="text-4xl lg:text-5xl xl:text-6xl font-black leading-[1.1] tracking-tight">
                    <span className="text-foreground">{isRTL ? 'حوّل موبايلك إلى' : 'Turn your phone into'}</span>
                    <br />
                    <span className="bg-gradient-to-r from-primary via-emerald-500 to-teal-500 bg-clip-text text-transparent">
                      {isRTL ? 'متجر إلكتروني!' : 'an online store!'}
                    </span>
                  </h1>
                </div>

                {/* Subtitle with Commission Highlight */}
                <div className="space-y-3">
                  <p className="text-xl text-muted-foreground leading-relaxed">
                    {isRTL ? 'اكسب حتى ' : 'Earn up to '}
                    <span className="font-black text-2xl text-[hsl(var(--ss-gold))] animate-pulse-glow inline-block">
                      20%
                    </span>
                    {isRTL ? ' عمولة على كل بيعة!' : ' commission on every sale!'}
                  </p>
                </div>

                {/* Benefits List */}
                <div className="flex flex-wrap gap-3">
                  {[
                    { icon: Package, text: isRTL ? 'بدون مخزون' : 'No inventory' },
                    { icon: Truck, text: isRTL ? 'بدون شحن' : 'No shipping' },
                    { icon: Target, text: isRTL ? 'بدون خبرة' : 'No experience' },
                  ].map((item, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 bg-white rounded-xl px-4 py-2.5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/10 to-emerald-100 flex items-center justify-center">
                        <item.icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="font-semibold text-gray-700">{item.text}</span>
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="flex gap-4 pt-4">
                  <div className="flex-1 hover:scale-[1.03] hover:-translate-y-1 active:scale-[0.97] transition-transform duration-200">
                    <Button 
                      size="lg" 
                      onClick={() => navigate(isAuthenticated ? '/marketplaces' : '/register')}
                      className="relative w-full h-16 text-lg font-bold rounded-2xl bg-gradient-to-r from-[hsl(var(--ss-gold))] via-amber-400 to-[hsl(var(--ss-gold-2))] text-black shadow-[0_10px_40px_rgba(234,179,8,0.4)] overflow-hidden group border-2 border-yellow-300/50"
                      data-testid="hero-cta-button"
                    >
                      <TrendingUp className="w-6 h-6 ml-2" />
                      <span className="relative z-10">{isRTL ? 'ابدأ الآن مجاناً' : 'Start Now Free'}</span>
                    </Button>
                  </div>
                  <div className="hover:scale-[1.03] hover:-translate-y-1 active:scale-[0.97] transition-transform duration-200">
                    <Button 
                      size="lg" 
                      variant="outline" 
                      onClick={() => navigate('/tutorials')}
                      className="h-16 px-8 text-lg font-bold rounded-2xl border-2 border-primary/40 text-primary hover:bg-primary/5 hover:border-primary transition-all gap-2"
                      data-testid="hero-learn-button"
                    >
                      {isRTL ? 'شاهد الفيديو' : 'Watch Video'}
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <ChevronLeft className="w-4 h-4" />
                      </div>
                    </Button>
                  </div>
                </div>

                {/* Social Proof */}
                <div className="flex items-center gap-4 pt-4">
                  <div className="flex -space-x-2 rtl:space-x-reverse">
                    {['👨‍💼', '👩‍💼', '👨‍💻', '👩‍💻', '🧑‍💼'].map((emoji, i) => (
                      <div 
                        key={i}
                        className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white flex items-center justify-center text-lg shadow-sm"
                      >
                        {emoji}
                      </div>
                    ))}
                  </div>
                  <div className="text-sm">
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                    </div>
                    <p className="text-muted-foreground">{isRTL ? '+3,500 تقييم إيجابي' : '+3,500 positive reviews'}</p>
                  </div>
                </div>
              </div>

              {/* Right Side - Phone Mockup with Products */}
              <div className="relative">
                {/* Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-emerald-200/30 to-teal-200/20 rounded-[60px] blur-3xl" />
                
                {/* Phone Frame */}
                <div className="relative mx-auto w-[320px]">
                  {/* Phone Border */}
                  <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-[40px] p-3 shadow-2xl">
                    {/* Notch */}
                    <div className="absolute top-5 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20" />
                    
                    {/* Screen */}
                    <div className="bg-gradient-to-b from-gray-50 to-white rounded-[32px] overflow-hidden">
                      {/* App Header */}
                      <div className="bg-gradient-to-r from-primary to-emerald-500 px-4 py-4 pt-8">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                              <span className="text-[8px] font-black text-white">S&S</span>
                            </div>
                            <span className="text-white font-bold text-sm">{isRTL ? 'متجري' : 'My Store'}</span>
                          </div>
                          <div className="flex items-center gap-1 bg-white/20 rounded-full px-2 py-1">
                            <DollarSign className="w-3 h-3 text-white" />
                            <span className="text-white text-xs font-bold">2,450</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Products Grid */}
                      <div className="p-3 space-y-2">
                        {/* Stats Bar */}
                        <div className="flex gap-2 mb-3">
                          <div className="flex-1 bg-emerald-50 rounded-xl p-2 text-center">
                            <p className="text-emerald-600 font-black text-lg">$847</p>
                            <p className="text-[10px] text-emerald-600/70">{isRTL ? 'أرباحي' : 'My Earnings'}</p>
                          </div>
                          <div className="flex-1 bg-blue-50 rounded-xl p-2 text-center">
                            <p className="text-blue-600 font-black text-lg">156</p>
                            <p className="text-[10px] text-blue-600/70">{isRTL ? 'مبيعات' : 'Sales'}</p>
                          </div>
                        </div>

                        {/* Animated Product Cards */}
                        <AnimatePresence mode="wait">
                          <motion.div 
                            key={heroSlide}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-2"
                          >
                            {[0, 1, 2].map((offset) => {
                              const productIndex = (heroSlide + offset) % HERO_SLIDER.length;
                              const product = HERO_SLIDER[productIndex];
                              const sellPrice = 25 + offset * 15;
                              const profit = Math.round(sellPrice * 0.2);
                              return (
                                <motion.div 
                                  key={productIndex}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: offset * 0.1 }}
                                  className="flex items-center gap-2 bg-white rounded-xl p-2 shadow-sm border border-gray-100"
                                >
                                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                    <img src={product.img} alt="" className="w-full h-full object-cover" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-gray-800 truncate">{product.label_ar}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-sm font-black text-gray-900">${sellPrice}</span>
                                      <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
                                        +${profit} {isRTL ? 'ربحك' : 'profit'}
                                      </span>
                                    </div>
                                  </div>
                                  <motion.div 
                                    whileHover={{ scale: 1.1 }}
                                    className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"
                                  >
                                    <Share2 className="w-4 h-4 text-white" />
                                  </motion.div>
                                </motion.div>
                              );
                            })}
                          </motion.div>
                        </AnimatePresence>

                        {/* Bottom Action */}
                        <div className="bg-gradient-to-r from-[hsl(var(--ss-gold))] to-amber-400 rounded-xl p-3 text-center mt-3">
                          <p className="text-black font-bold text-sm">{isRTL ? '🎁 شارك واكسب!' : '🎁 Share & Earn!'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements - CSS Animations */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl shadow-xl flex items-center justify-center animate-gentle-bounce">
                  <div className="text-center">
                    <p className="text-white font-black text-xl">20%</p>
                    <p className="text-white/80 text-[10px] font-bold">{isRTL ? 'عمولة' : 'Commission'}</p>
                  </div>
                </div>

                <div className="absolute -bottom-2 -left-6 w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl shadow-xl flex items-center justify-center animate-gentle-bounce" style={{animationDelay: '0.5s'}}>
                  <Gift className="w-8 h-8 text-white" />
                </div>

                <div className="absolute top-1/2 -left-10 w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl shadow-xl flex items-center justify-center animate-gentle-bounce" style={{animationDelay: '1s'}}>
                  <Trophy className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* ── 3. HERO TEXT - REMOVED (now in mobile V17 section) ── */}
          <div className="hidden px-5 sm:px-10 pt-6 sm:pt-10 pb-5">
            <div className="max-w-3xl">
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3 leading-[1.2]">
                <span className="text-foreground">{isRTL ? 'بِع منتجات واكسب ' : 'Sell Products & Earn '}</span>
                <span className="bg-gradient-to-r from-[hsl(var(--ss-gold))] to-amber-400 bg-clip-text text-transparent">{isRTL ? 'عمولة 20%' : '20% Commission'}</span>
                <span className="text-foreground">{isRTL ? ' بدون مخزون!' : ' Without Inventory!'}</span>
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 leading-relaxed max-w-lg">
                {isRTL ? 'اختر المنتجات من كتالوجنا، شارك رابط متجرك — نحن نخزّن، نغلّف، ونوصّل. أنت فقط تبيع وتكسب العمولة!' : 'Pick from our catalog, share your store link — we store, pack, and deliver. You just sell and earn!'}
              </p>
              <div className="flex flex-row gap-3">
                <motion.div
                  animate={{ boxShadow: ['0 0 0px rgba(234,179,8,0)', '0 0 30px rgba(234,179,8,0.4)', '0 0 0px rgba(234,179,8,0)'] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  whileHover={{ scale: 1.05, y: -4 }} whileTap={{ scale: 0.94 }} className="rounded-2xl flex-1">
                  <Button size="lg" onClick={() => navigate(isAuthenticated ? '/marketplaces' : '/register')}
                    className="relative w-full h-14 px-4 sm:px-8 text-sm sm:text-base font-bold rounded-2xl bg-gradient-to-r from-[hsl(var(--ss-gold))] to-[hsl(var(--ss-gold-2))] text-black shadow-[0_8px_30px_rgba(234,179,8,0.35)] overflow-hidden group/btn gap-2 border-2 border-yellow-300/40"
                    data-testid="hero-cta-button">
                    <motion.div animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
                      <TrendingUp className="w-5 h-5" />
                    </motion.div>
                    <span className="relative z-10">{isRTL ? 'ابدأ التجارة' : 'Start Trading'}</span>
                    <span className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -4, boxShadow: '0 0 25px rgba(20,184,166,0.3)' }}
                  whileTap={{ scale: 0.94 }} className="rounded-2xl flex-1">
                  <Button size="lg" variant="outline" onClick={() => navigate('/tutorials')}
                    className="w-full h-14 px-4 sm:px-8 text-sm sm:text-base font-bold rounded-2xl border-2 border-primary/50 text-primary hover:bg-primary/10 hover:border-primary/70 transition-all gap-2 shadow-[0_4px_20px_rgba(20,184,166,0.1)]"
                    data-testid="hero-learn-button">
                    {isRTL ? 'كيف تبدأ؟' : 'How?'}<Arrow className="w-5 h-5" />
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>

          {/* ── HOW IT WORKS SECTION - Desktop Only ── */}
          <div className="hidden lg:block bg-gradient-to-b from-gray-50/50 to-white pt-8 pb-6 px-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-black text-gray-900 mb-2">
                  {isRTL ? 'كيف يعمل النظام؟' : 'How Does It Work?'}
                </h2>
                <p className="text-muted-foreground">
                  {isRTL ? '4 خطوات بسيطة لبدء الربح' : '4 simple steps to start earning'}
                </p>
              </div>

              <div className="grid grid-cols-4 gap-6 relative">
                {/* Connecting Line */}
                <div className="absolute top-16 left-[12%] right-[12%] h-1 bg-gradient-to-r from-primary via-emerald-400 to-amber-400 rounded-full hidden lg:block" />
                
                {[
                  { 
                    step: '1', 
                    icon: Package, 
                    title: isRTL ? 'اختر المنتجات' : 'Choose Products',
                    desc: isRTL ? 'تصفح آلاف المنتجات من كتالوجنا واختر ما يناسب جمهورك' : 'Browse thousands of products and pick what suits your audience',
                    color: 'from-primary to-emerald-500'
                  },
                  { 
                    step: '2', 
                    icon: Share2, 
                    title: isRTL ? 'شارك الرابط' : 'Share Your Link',
                    desc: isRTL ? 'انشر رابط متجرك الخاص على وسائل التواصل الاجتماعي' : 'Post your unique store link on social media',
                    color: 'from-emerald-500 to-teal-500'
                  },
                  { 
                    step: '3', 
                    icon: ShoppingCart, 
                    title: isRTL ? 'عميل يشتري' : 'Customer Buys',
                    desc: isRTL ? 'نحن نتولى الشحن والتغليف والتوصيل - أنت لا تفعل شيء!' : 'We handle shipping, packing & delivery - you do nothing!',
                    color: 'from-teal-500 to-cyan-500'
                  },
                  { 
                    step: '4', 
                    icon: DollarSign, 
                    title: isRTL ? 'اكسب العمولة' : 'Earn Commission',
                    desc: isRTL ? 'تحصل على 20% عمولة فورية على كل عملية بيع!' : 'Get 20% commission instantly on every sale!',
                    color: 'from-amber-400 to-orange-500'
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="relative text-center group"
                  >
                    {/* Step Number Circle */}
                    <div
                      className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${item.color} shadow-lg flex items-center justify-center relative z-10 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}
                    >
                      <item.icon className="w-7 h-7 text-white" />
                      <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-sm font-black text-gray-800">
                        {item.step}
                      </span>
                    </div>
                    
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── 4. QUICK CATEGORY ROW - ALL 20 CATEGORIES - Desktop Only ── */}
          <div className="hidden lg:block px-4 sm:px-10 pt-4 pb-4">
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-3 sm:gap-4">
              {[
                // أقسام نسائية
                { icon: '👜', ar: 'اكسسوارات نسائية', cat: 'accessories' },
                { icon: '👗', ar: 'أزياء نسائية', cat: 'fashion' },
                { icon: '💄', ar: 'جمال وصحة', cat: 'beauty' },
                { icon: '💎', ar: 'مجوهرات', cat: 'jewelry' },
                // أقسام رجالية
                { icon: '🕶️', ar: 'اكسسوارات رجالية', cat: 'men_accessories' },
                { icon: '👔', ar: 'أزياء رجالية', cat: 'men_fashion' },
                { icon: '🧴', ar: 'عناية رجالية', cat: 'men_grooming' },
                { icon: '⌚', ar: 'ساعات رجالية', cat: 'men_watches' },
                // أقسام مشتركة
                { icon: '📱', ar: 'إلكترونيات', cat: 'electronics' },
                { icon: '🏠', ar: 'المنزل والمطبخ', cat: 'home' },
                { icon: '⚽', ar: 'رياضة ولياقة', cat: 'sports' },
                { icon: '🎮', ar: 'ألعاب', cat: 'toys' },
                { icon: '📲', ar: 'هواتف وأجهزة', cat: 'phones' },
                { icon: '💻', ar: 'حواسيب', cat: 'computers' },
                { icon: '💪', ar: 'مكملات غذائية', cat: 'supplements' },
                { icon: '🐾', ar: 'مستلزمات حيوانات', cat: 'pet' },
                { icon: '👶', ar: 'أطفال ورضع', cat: 'baby' },
                { icon: '🌿', ar: 'حديقة وخارجي', cat: 'garden' },
                { icon: '🚗', ar: 'سيارات', cat: 'automotive' },
                { icon: '📎', ar: 'مستلزمات مكتبية', cat: 'office' },
              ].map((c, i) => (
                <button key={i}
                  onClick={() => navigate(`/products?category=${c.cat}`)}
                  className="flex flex-col items-center gap-2 hover:-translate-y-1 hover:scale-105 active:scale-95 transition-all duration-200">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-card border border-border/50 flex items-center justify-center text-2xl sm:text-3xl shadow-sm hover:shadow-lg hover:border-primary/30 transition-all">
                    {c.icon}
                  </div>
                  <span className="text-xs sm:text-sm text-muted-foreground font-medium text-center leading-tight line-clamp-2">{isRTL ? c.ar : c.cat}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── DYNAMIC PRODUCT CAROUSEL (70+ real products) - Desktop Only ── */}
          <div className="hidden lg:block pt-4 pb-4">
            <div className="flex items-center gap-2 px-5 sm:px-10 mb-3">
              <Store className="w-3.5 h-3.5 text-primary" />
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">{isRTL ? 'منتجات مميزة وعروض حصرية' : 'Featured Products & Exclusive Deals'}</p>
              <span className="text-[9px] text-muted-foreground/50 font-mono">70+</span>
            </div>
            <div className="relative px-5 sm:px-10">
              <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide" id="hero-products-scroll">
                {HERO_SLIDER.map((p, i) => (
                  <div key={i} className="flex-shrink-0 w-40 sm:w-48 cursor-pointer group/card hover:-translate-y-1 transition-transform duration-200"
                    onClick={() => navigate(`/products?category=${p.cat || 'accessories'}`)}>
                    <div className="relative rounded-xl overflow-hidden ring-1 ring-border/50 group-hover/card:ring-primary/40 transition-all shadow-sm group-hover/card:shadow-lg group-hover/card:shadow-primary/5">
                      <div className="aspect-square overflow-hidden bg-muted/10">
                        <img src={p.img} alt="" className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-500" loading="lazy" />
                      </div>
                      {p.badge && (
                        <span className={`absolute top-2 left-2 ${p.badgeColor} text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md shadow`}>{p.badge}</span>
                      )}
                      {/* S&S Logo - top right corner */}
                      <div className="absolute top-2 right-2 w-7 h-7 rounded-md bg-gradient-to-br from-primary to-emerald-400 flex items-center justify-center shadow-md border border-white/20">
                        <span className="text-[7px] font-black text-white leading-none">S&S</span>
                      </div>
                    </div>
                    <div className="mt-1.5 px-0.5">
                      <p className="text-[11px] font-medium text-foreground truncate">{p.label_ar}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="absolute left-1 top-1/3 w-8 h-8 rounded-full bg-card/90 shadow-lg border border-border flex items-center justify-center opacity-50 hover:opacity-100 z-10"
                onClick={() => document.getElementById('hero-products-scroll')?.scrollBy({ left: -350, behavior: 'smooth' })}><ChevronLeft className="w-4 h-4" /></button>
              <button className="absolute right-1 top-1/3 w-8 h-8 rounded-full bg-card/90 shadow-lg border border-border flex items-center justify-center opacity-50 hover:opacity-100 z-10"
                onClick={() => document.getElementById('hero-products-scroll')?.scrollBy({ left: 350, behavior: 'smooth' })}><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>

          {/* ── DIVIDER - Desktop Only ── */}
          <div className="hidden lg:block h-px mx-5 sm:mx-10 bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* ── WAREHOUSE STRIP - Desktop Only ── */}
          <div className="hidden lg:block pt-4 pb-4 overflow-hidden">
            <div className="px-5 sm:px-10 mb-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Warehouse className="w-4 h-4 text-primary" />
                <h3 className="text-sm sm:text-base font-bold text-foreground">{isRTL ? 'من داخل مستودعاتنا' : 'Inside Our Warehouses'}</h3>
              </div>
              <p className="text-[11px] sm:text-xs text-muted-foreground mb-3">{isRTL ? 'مستودعات حقيقية في 5 دول — نخزّن ونغلّف ونشحن منتجاتك بأعلى معايير الجودة' : 'Real warehouses in 5 countries — storing, packing & shipping with top quality'}</p>
              <div className="flex gap-3">
                {[
                  { val: '48', lbl: isRTL ? 'قسم' : 'Sections' },
                  { val: '8', lbl: isRTL ? 'مستودع' : 'Warehouses' },
                  { val: '5', lbl: isRTL ? 'دول' : 'Countries' },
                ].map((s, i) => (
                  <div key={i} className="flex items-baseline gap-1">
                    <span className="text-sm font-bold text-primary font-mono">{s.val}</span>
                    <span className="text-[10px] text-muted-foreground">{s.lbl}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative" style={{ direction: 'ltr' }}>
              <div className="flex gap-3 showcase-scroll" style={{ width: 'max-content' }}>
                {[...SHOWCASE_STRIP, ...SHOWCASE_STRIP].map((s, i) => (
                  <div key={i} className="relative flex-shrink-0 w-52 sm:w-60 h-40 sm:h-48 rounded-xl overflow-hidden ring-1 ring-border/50 group cursor-pointer hover:ring-primary/40 transition-all shadow-sm hover:shadow-lg"
                    onClick={() => setWarehousePopup(s)}>
                    <img src={s.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    {/* S&S logo */}
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-md bg-gradient-to-br from-primary to-emerald-400 flex items-center justify-center shadow border border-white/20">
                      <span className="text-[6px] font-black text-white">S&S</span>
                    </div>
                    {/* Zoom icon */}
                    <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20">
                      <Zap className="w-3.5 h-3.5 text-white" />
                    </div>
                    {/* Label */}
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-xs font-bold text-white drop-shadow-lg">{s.label_ar}</p>
                      <p className="text-[9px] text-white/50">SHOP & SHIP</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── DIVIDER - Desktop Only ── */}
          <div className="hidden lg:block h-px mx-5 sm:mx-10 bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* ── HOW IT WORKS - DETAILED - Desktop Only ── */}
          <div className="hidden lg:block px-5 sm:px-10 lg:px-14 pt-4 pb-4">
            <div className="text-center mb-4">
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground mb-1">{isRTL ? 'كيف تصبح تاجراً من بيتك؟' : 'How to Become a Home Seller?'}</h2>
              <p className="text-xs sm:text-sm text-muted-foreground">{isRTL ? 'بدون مخزون، بدون شحن، بدون خبرة سابقة — فقط 5 خطوات' : 'No inventory, no shipping, no experience — just 5 steps'}</p>
            </div>
            {/* Steps 1-4 grid */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {[
                { n: '01', icon: CreditCard, t: isRTL ? 'اشحن رصيدك' : 'Top Up', d: isRTL ? 'اشترِ عملات S&S بالـ USDT — طريقة دفع آمنة وفورية بدون وسطاء أو رسوم بنكية' : 'Buy S&S Coins with USDT — secure, instant, no bank fees', clr: '#EAB308' },
                { n: '02', icon: Store, t: isRTL ? 'اختر منتجاتك' : 'Pick Products', d: isRTL ? 'تصفّح +2000 منتج في كتالوجنا واشترِ بعملات S&S — المنتج يُضاف لمتجرك الخاص تلقائياً' : 'Browse 2000+ products, buy with S&S Coins — auto-added to your store', clr: '#14b8a6' },
                { n: '03', icon: Share2, t: isRTL ? 'سوّق وبِع' : 'Market & Sell', d: isRTL ? 'شارك رابط متجرك على فيسبوك، إنستغرام، تيك توك، واتساب — عندما يشتري أحد، أنت تكسب!' : 'Share on FB, IG, TikTok, WhatsApp — when someone buys, you earn!', clr: '#3b82f6' },
                { n: '04', icon: Truck, t: isRTL ? 'نحن نتكفّل بالباقي' : 'We Handle the Rest', d: isRTL ? 'نخزّن المنتج في مستودعاتنا، نغلّفه باحترافية، ونشحنه مباشرة لباب المشتري' : 'We store, pack professionally & ship directly to the buyer', clr: '#a855f7' },
              ].map((step, i) => (
                <div key={i}
                  className="rounded-xl p-4 border-2 relative overflow-hidden bg-gradient-to-b hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                  style={{ borderColor: `${step.clr}25`, background: `linear-gradient(to bottom, ${step.clr}08, transparent)` }}>
                  <span className="absolute -bottom-1 -left-1 text-[50px] font-black leading-none opacity-[0.04] font-mono">{step.n}</span>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${step.clr}15`, border: `1.5px solid ${step.clr}30` }}>
                        <step.icon className="w-4 h-4" style={{ color: step.clr }} />
                      </div>
                      <span className="text-[10px] font-mono font-bold opacity-20">{step.n}</span>
                    </div>
                    <h3 className="text-sm font-bold text-foreground mb-1">{step.t}</h3>
                    <p className="text-[10px] text-muted-foreground leading-relaxed">{step.d}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Step 5 - special golden */}
            <div
              
              className="rounded-xl p-4 border-2 border-[hsl(var(--ss-gold)/0.3)] relative overflow-hidden bg-gradient-to-r from-[hsl(var(--ss-gold)/0.08)] via-[hsl(var(--ss-gold)/0.04)] to-transparent ring-1 ring-[hsl(var(--ss-gold)/0.15)] hover:shadow-lg hover:shadow-[hsl(var(--ss-gold)/0.1)] transition-all">
              <span className="absolute -bottom-1 -left-1 text-[50px] font-black leading-none opacity-[0.04] font-mono">05</span>
              <div className="relative flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[hsl(var(--ss-gold)/0.15)] border-[1.5px] border-[hsl(var(--ss-gold)/0.3)]">
                  <TrendingUp className="w-5 h-5 text-[hsl(var(--ss-gold))]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-foreground">{isRTL ? 'اكسب واترقَّ!' : 'Earn & Level Up!'}</h3>
                  <p className="text-[10px] text-muted-foreground leading-relaxed">{isRTL ? 'تحصل على فارق الربح + عمولة إضافية على كل بيعة. كلّما بعت أكثر، ترتقي بمستواك وتزيد نسبة عمولتك — حقق أهداف المبيعات واكسب مكافآت حصرية!' : 'Get profit margin + bonus commission per sale. Sell more = higher tier = bigger commission. Hit sales goals for exclusive rewards!'}</p>
                </div>
                <span className="text-[10px] font-mono font-bold opacity-20">05</span>
              </div>
            </div>
          </div>

          {/* ── MERGED CTA SECTION - Desktop Only ── */}
          <div className="hidden lg:block px-5 sm:px-10 lg:px-14 pt-4 pb-6">
            <div className="text-center mb-4">
              <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-foreground leading-tight mb-2">
                {isRTL ? 'المجالات مفتوحة! ابدأ بالتجارة من منزلك' : 'Start trading from home'}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">{isRTL ? 'لا تحتاج مكتب ولا مخزون — فقط هاتفك وطموحك!' : "No office — just your phone!"}</p>
            </div>
            {/* Feature pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {[
                { icon: Target, t: isRTL ? 'مهام يومية' : 'Tasks', d: isRTL ? 'نفّذ واكسب' : 'Do & earn', clr: '#14b8a6' },
                { icon: Trophy, t: isRTL ? 'عمولات أعلى' : 'Tiers', d: isRTL ? 'ارتقِ بمستواك' : 'Level up', clr: '#EAB308' },
                { icon: Gift, t: isRTL ? 'مكافآت' : 'Rewards', d: isRTL ? 'بونصات إضافية' : 'Extra bonuses', clr: '#a855f7' },
              ].map((task, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-card border border-border/50 rounded-xl px-4 py-3 shadow-sm hover:shadow-md hover:border-primary/30 transition-all">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${task.clr}15`, border: `1.5px solid ${task.clr}30` }}>
                    <task.icon className="w-4.5 h-4.5" style={{ color: task.clr }} />
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-foreground">{task.t}</p>
                    <p className="text-[9px] text-muted-foreground">{task.d}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* CTA Button */}
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-2xl hover:scale-105 hover:-translate-y-1 active:scale-95 transition-all duration-200">
                <Button size="lg" onClick={() => navigate(isAuthenticated ? '/buy-coins' : '/register')}
                  className="relative h-14 sm:h-15 px-10 sm:px-12 text-base sm:text-lg font-bold rounded-2xl bg-gradient-to-r from-[hsl(var(--ss-gold))] to-[hsl(var(--ss-gold-2))] text-black shadow-xl gap-2.5 border-2 border-yellow-300/30 overflow-hidden group/cta animate-pulse-glow"
                  data-testid="hero-buy-coins-button">
                  <DollarSign className="w-5 h-5" />
                  {isRTL ? 'اشترِ عملاتك وابدأ!' : 'Buy Coins & Start!'}
                  <span>{isRTL ? '←' : '→'}</span>
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">{isRTL ? 'انضم لأكثر من 50,000 بائع نشط' : 'Join 50,000+ active sellers'}</p>
            </div>
          </div>
        </div>

      {/* Warehouse Image Popup */}
      <AnimatePresence>
        {warehousePopup && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setWarehousePopup(null)}>
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 20 }}
              className="relative max-w-4xl w-full rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
              <img src={warehousePopup.img} alt="" className="w-full h-auto max-h-[80vh] object-contain bg-black" />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white text-lg font-bold">{warehousePopup.label_ar}</p>
                <p className="text-white/50 text-sm">SHOP & SHIP</p>
              </div>
              <button onClick={() => setWarehousePopup(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 text-xl backdrop-blur-sm border border-white/10">&times;</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== SHOPPING SECTION ===== */}
      {(homeSections.length > 0 || featuredProducts.length > 0) && (<>

      {/* Cart bounce triggers topbar animation via localStorage event */}

      <div className="h-4" />

      {/* Hot Deals Dark Banner - with sliding arrows */}
      {dailyDeals.length > 0 && (
        <section className="relative group/deals">
          <Card className="overflow-hidden border-0 shadow-2xl rounded-2xl">
            <div className="bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] p-6 sm:p-8 relative">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(45,212,191,0.08),transparent_60%)]" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-4xl">🔥</span>
                  <h2 className="text-2xl font-bold text-white">{isRTL?'عروض اليوم':'Today\'s Deals'}</h2>
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">{isRTL?'محدود':'Limited'}</Badge>
                </div>
                <div className="relative">
                  <div className="flex gap-5 overflow-x-auto pb-3 scrollbar-hide" id="deals-scroll">
                    {dailyDeals.flatMap(c=>(c.products||[]).filter(Boolean)).slice(0,12).map((p,i)=>(
                      <div key={p.product_id||i} className="flex-shrink-0 w-52 bg-card rounded-2xl overflow-hidden cursor-pointer group/item shadow-lg hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300"
                        onClick={()=>navigate(`/product/${p.product_id}`)}>
                        <div className="relative overflow-hidden">
                          <img src={p.image} alt="" className="w-52 h-52 object-cover group-hover/item:scale-110 transition-transform duration-500"/>
                          {p.original_price>0&&<Badge className="absolute top-3 left-3 bg-red-500 text-white border-0 text-xs font-bold shadow-lg px-2.5 py-1">-{Math.round((1-p.sell_price/p.original_price)*100)}%</Badge>}
                          {/* Hover overlay with product info */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover/item:opacity-100 transition-all duration-300 flex flex-col justify-end p-3">
                            <div className="flex items-center gap-0.5 mb-1">{[1,2,3,4,5].map(s=><Star key={s} className="w-3 h-3 fill-amber-400 text-amber-400"/>)}<span className="text-white/80 text-[9px] ml-1">4.8</span></div>
                            <p className="text-white text-xs font-medium line-clamp-1 mb-2">{isRTL?p.name_ar:p.name}</p>
                            <Button size="sm" className="w-full h-8 text-xs gap-1.5 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white font-bold shadow-lg border-0" onClick={e=>{e.stopPropagation();addToCartAnimated({...p,id:p.product_id})}}>
                              <ShoppingCart className="w-3.5 h-3.5"/>{isRTL?'أضف للسلة':'Add to Cart'}
                            </Button>
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="text-xs font-medium line-clamp-1 mb-1.5">{isRTL?p.name_ar:p.name}</p>
                          <div className="flex items-baseline gap-2"><span className="text-lg font-bold">${p.sell_price}</span>{p.original_price>0&&<span className="text-xs text-muted-foreground line-through">${p.original_price.toFixed(0)}</span>}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 shadow-2xl flex items-center justify-center opacity-70 group-hover/deals:opacity-100 transition-all z-10 hover:bg-white hover:scale-110"
                    onClick={()=>document.getElementById('deals-scroll')?.scrollBy({left:-400,behavior:'smooth'})}>
                    <ChevronLeft className="w-6 h-6"/>
                  </button>
                  <button className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 shadow-2xl flex items-center justify-center opacity-70 group-hover/deals:opacity-100 transition-all z-10 hover:bg-white hover:scale-110"
                    onClick={()=>document.getElementById('deals-scroll')?.scrollBy({left:400,behavior:'smooth'})}>
                    <ChevronRight className="w-6 h-6"/>
                  </button>
                </div>
              </div>
            </div>
          </Card>
        </section>
      )}

      {/* Render sections dynamically */}
      {(()=>{
        const rows = {};
        const strips = [];
        homeSections.forEach(s => {
          if (s.type === 'category_card') {
            if (!rows[s.row]) rows[s.row] = [];
            rows[s.row].push(s);
          } else if (s.type === 'horizontal_strip') {
            strips.push(s);
          }
        });
        const bannerBgs = ['linear-gradient(135deg,hsl(173 30% 92%),hsl(173 25% 88%),hsl(45 30% 92%))','linear-gradient(135deg,hsl(45 60% 92%),hsl(45 40% 88%),hsl(30 40% 92%))','linear-gradient(135deg,hsl(220 40% 93%),hsl(260 30% 92%),hsl(220 30% 94%))'];
        const output = [];
        let stripIdx = 0;

        Object.keys(rows).sort().forEach((rowKey, ri) => {
          const rowCats = rows[rowKey];
          output.push(
            <section key={`row-${rowKey}`} className="relative -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4" style={{background:bannerBgs[ri % bannerBgs.length]}}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-[1200px] mx-auto">
                {rowCats.map((cat,ci)=>(
                  <Card key={cat.id||ci} className="bg-card shadow-lg hover:shadow-2xl transition-all duration-300 border border-border/30 rounded-2xl group">
                    <CardContent className="p-4 sm:p-5">
                      <h3 className="font-bold text-base sm:text-lg mb-3 text-foreground">{isRTL?cat.title_ar:cat.title}</h3>
                      <div className="grid grid-cols-2 gap-2.5 mb-3">
                        {(cat.products||[]).slice(0,4).map((p,pi)=>p?(
                          <div key={p.product_id||pi} className="cursor-pointer group/item" onClick={()=>setProductPopup(p)}>
                            <div className="rounded-xl overflow-hidden bg-muted/10 aspect-square mb-1.5 relative ring-1 ring-border/30 group-hover/item:ring-primary/40 transition-all">
                              <img src={p.image} alt="" className="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-300"/>
                              {p.original_price > 0 && p.sell_price < p.original_price && (
                                <span className="absolute top-1.5 left-1.5 bg-red-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-md shadow">-{Math.round((1 - p.sell_price / p.original_price) * 100)}%</span>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground group-hover/item:text-primary transition-colors line-clamp-1">{isRTL?(cat.subs_ar||[])[pi]:(cat.subs||[])[pi]}</p>
                            <span className="text-xs font-bold text-primary font-mono">${p.sell_price}</span>
                          </div>
                        ):(<div key={pi} className="aspect-square bg-muted/10 rounded-xl"/>))}
                      </div>
                      <button onClick={()=>navigate('/products')} className="w-full h-10 rounded-xl bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20 transition-colors flex items-center justify-center gap-1.5 border border-primary/20">
                        {isRTL?cat.link_ar:cat.link} <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          );

          // Insert horizontal strip after each row
          if (strips[stripIdx]) {
            const strip = strips[stripIdx];
            stripIdx++;
            output.push(
              <section key={strip.id} className="bg-card rounded-2xl p-4 sm:p-5 shadow-lg relative group/strip overflow-hidden border border-border/40">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base sm:text-lg font-bold text-foreground">{isRTL?strip.title_ar:strip.title}</h2>
                  <Button variant="outline" className="text-xs text-primary border-primary/20 h-8 px-3 rounded-lg" onClick={()=>navigate('/products')}>{isRTL?'عرض المزيد':'See more'} <ChevronLeft className="w-3 h-3 mr-1" /></Button>
                </div>
                <div className="relative">
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" id={`strip-${strip.id}`}>
                    {(strip.products||[]).filter(Boolean).map((p,i)=>(
                      <motion.div key={p.product_id||i} className="flex-shrink-0 w-44 sm:w-52 cursor-pointer group/prod" onClick={()=>navigate(`/product/${p.product_id}`)}
                        whileHover={{scale:1.03,y:-4}} transition={{type:'spring',stiffness:300}}>
                        <div className="relative rounded-xl overflow-hidden shadow-sm group-hover/prod:shadow-lg transition-shadow ring-1 ring-border/30">
                          <img src={p.image} alt="" className="w-full aspect-square object-cover"/>
                          {p.original_price > 0 && p.sell_price < p.original_price && (
                            <span className="absolute top-2 left-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md">-{Math.round((1-p.sell_price/p.original_price)*100)}%</span>
                          )}
                        </div>
                        <div className="mt-2 px-1">
                          <p className="text-[11px] font-medium text-foreground line-clamp-1">{isRTL?p.name_ar:p.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs font-bold text-primary font-mono">${p.sell_price}</span>
                            <Button size="sm" className="h-7 text-[9px] gap-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 px-2" onClick={e=>{e.stopPropagation();addToCartAnimated({...p,id:p.product_id})}}>
                              <ShoppingCart className="w-3 h-3"/>{isRTL?'أضف':'Add'}
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <button className="absolute left-0 top-1/3 w-8 h-8 rounded-full bg-card/90 shadow-lg border border-border flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity z-10"
                    onClick={()=>{const el=document.getElementById(`strip-${strip.id}`);if(el)el.scrollBy({left:-300,behavior:'smooth'})}}>
                    <ChevronLeft className="w-4 h-4"/>
                  </button>
                  <button className="absolute right-0 top-1/3 w-8 h-8 rounded-full bg-card/90 shadow-lg border border-border flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity z-10"
                    onClick={()=>{const el=document.getElementById(`strip-${strip.id}`);if(el)el.scrollBy({left:300,behavior:'smooth'})}}>
                    <ChevronRight className="w-4 h-4"/>
                  </button>
                </div>
              </section>
            );
          }
        });

        // Remaining strips
        while (stripIdx < strips.length) {
          const strip = strips[stripIdx];
          stripIdx++;
          output.push(
            <section key={strip.id} className="bg-card rounded-2xl p-5 sm:p-6 shadow-lg relative group/strip overflow-hidden border border-border/40">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base sm:text-lg font-bold text-foreground">{isRTL?strip.title_ar:strip.title}</h2>
                <Button variant="outline" className="text-xs text-primary border-primary/20 h-8 px-3 rounded-lg" onClick={()=>navigate('/products')}>{isRTL?'عرض المزيد':'See more'} <ChevronLeft className="w-3 h-3 mr-1" /></Button>
              </div>
              <div className="relative">
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" id={`strip-${strip.id}`}>
                  {(strip.products||[]).filter(Boolean).map((p,i)=>(
                    <motion.div key={p.product_id||i} className="flex-shrink-0 w-44 sm:w-52 cursor-pointer group/prod" onClick={()=>navigate(`/product/${p.product_id}`)}
                      whileHover={{scale:1.03,y:-4}} transition={{type:'spring',stiffness:300}}>
                      <div className="relative rounded-xl overflow-hidden shadow-sm group-hover/prod:shadow-lg transition-shadow ring-1 ring-border/30">
                        <img src={p.image} alt="" className="w-full aspect-square object-cover"/>
                        {p.original_price > 0 && p.sell_price < p.original_price && (
                          <span className="absolute top-2 left-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md">-{Math.round((1-p.sell_price/p.original_price)*100)}%</span>
                        )}
                      </div>
                      <div className="mt-2 px-1">
                        <p className="text-[11px] font-medium text-foreground line-clamp-1">{isRTL?p.name_ar:p.name}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs font-bold text-primary font-mono">${p.sell_price}</span>
                          <Button size="sm" className="h-7 text-[9px] gap-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 px-2" onClick={e=>{e.stopPropagation();addToCartAnimated({...p,id:p.product_id})}}>
                            <ShoppingCart className="w-3 h-3"/>{isRTL?'أضف':'Add'}
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <button className="absolute left-0 top-1/3 w-8 h-8 rounded-full bg-card/90 shadow-lg border border-border flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity z-10"
                  onClick={()=>{const el=document.getElementById(`strip-${strip.id}`);if(el)el.scrollBy({left:-300,behavior:'smooth'})}}>
                  <ChevronLeft className="w-4 h-4"/>
                </button>
                <button className="absolute right-0 top-1/3 w-8 h-8 rounded-full bg-card/90 shadow-lg border border-border flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity z-10"
                  onClick={()=>{const el=document.getElementById(`strip-${strip.id}`);if(el)el.scrollBy({left:300,behavior:'smooth'})}}>
                  <ChevronRight className="w-4 h-4"/>
                </button>
              </div>
            </section>
          );
        }

        return output;
      })()}

      {/* Explore CTA */}
      <section className="mt-8 mb-12">
        <Card className="bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] border-0 rounded-2xl shadow-2xl overflow-hidden relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(45,212,191,0.1),transparent_70%)]" />
          <CardContent className="p-8 text-center relative z-10">
            <div className="text-5xl mb-3 animate-gentle-bounce inline-block">🛍</div>
            <h2 className="text-3xl font-bold text-white mb-2">{isRTL?'اكتشف أكثر من 50,000 منتج!':'Discover 50,000+ Products!'}</h2>
            <p className="text-white/60 text-base mb-5">{isRTL?'تصفح كتالوجنا الكامل بأسعار تنافسية':'Browse our full catalog at competitive prices'}</p>
            <div className="hover:scale-105 active:scale-95 transition-transform duration-200 inline-block">
              <Button size="lg" onClick={()=>navigate('/products')} className="gap-3 h-14 px-12 bg-gradient-to-r from-primary to-emerald-500 text-white font-bold rounded-2xl shadow-xl shadow-primary/30 text-lg">
                <Store className="w-6 h-6"/>{isRTL?'تصفح المنتجات':'Browse Products'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      </>)}

      {/* Product Popup Card */}
      <AnimatePresence>
        {productPopup && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setProductPopup(null)}>
            <motion.div initial={{scale:0.7,y:50,opacity:0}} animate={{scale:1,y:0,opacity:1}} exit={{scale:0.8,y:30,opacity:0}}
              transition={{type:'spring',stiffness:400,damping:20}}
              className="bg-card rounded-3xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e=>e.stopPropagation()}>
              {/* Product Image */}
              <div className="relative overflow-hidden">
                <img src={productPopup.image} alt="" className="w-full h-72 object-cover"/>
                {productPopup.original_price > 0 && (
                  <Badge className="absolute top-4 left-4 bg-red-500 text-white border-0 text-sm font-bold shadow-xl px-3 py-1.5">
                    -{Math.round((1 - productPopup.sell_price / productPopup.original_price) * 100)}% {isRTL ? 'خصم' : 'OFF'}
                  </Badge>
                )}
                <button onClick={()=>setProductPopup(null)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors">
                  <span className="text-lg leading-none">&times;</span>
                </button>
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-card to-transparent" />
              </div>
              {/* Product Info */}
              <div className="p-6 -mt-6 relative">
                <h3 className="text-lg font-bold mb-2">{isRTL ? productPopup.name_ar : productPopup.name}</h3>
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                  <span className="text-sm text-muted-foreground ml-1">4.8 ({Math.floor(Math.random() * 5000 + 1000).toLocaleString()} {isRTL ? 'تقييم' : 'reviews'})</span>
                </div>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-3xl font-bold font-mono">${productPopup.sell_price}</span>
                  {productPopup.original_price > 0 && <span className="text-lg text-muted-foreground line-through">${productPopup.original_price.toFixed(0)}</span>}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-5">
                  <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5 text-primary" />{isRTL ? 'شحن مجاني' : 'Free Shipping'}</span>
                  <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-emerald-500" />{isRTL ? 'ضمان جودة' : 'Quality Guaranteed'}</span>
                </div>
                <div className="flex gap-3">
                  <Button className="flex-1 h-12 gap-2 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white font-bold text-base shadow-lg shadow-primary/30"
                    onClick={()=>{addToCartAnimated({...productPopup,id:productPopup.product_id});setProductPopup(null)}}>
                    <ShoppingCart className="w-5 h-5" />{isRTL ? 'أضف للسلة' : 'Add to Cart'}
                  </Button>
                  <Button variant="outline" className="h-12 px-5 rounded-xl" onClick={()=>{setProductPopup(null);navigate(`/product/${productPopup.product_id}`)}}>
                    {isRTL ? 'التفاصيل' : 'Details'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats */}
      <section>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
            >
              <Card className="bg-card border-border hover:border-primary/20 transition-colors">
                <CardContent className="p-6 text-center">
                  <p className="font-mono text-2xl sm:text-3xl font-bold text-primary mb-1">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it Works */}
      <section className="mb-12 mt-16">
        <div className="text-center mb-8">
          <h2 className="font-heading text-3xl font-bold mb-3">{t('howItWorks')}</h2>
          <p className="text-muted-foreground">
            {isRTL ? 'أربع خطوات بسيطة لبدء الكسب' : 'Four simple steps to start earning'}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -8, scale: 1.03 }}
              >
                <Card className="bg-card border-border hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 group relative overflow-hidden cursor-pointer">
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:to-transparent transition-all duration-500" />
                  {/* Top shine line on hover */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/0 to-transparent group-hover:via-primary/60 transition-all duration-500" />
                  <CardContent className="p-6 relative">
                    <div className="absolute top-4 right-4 text-6xl font-bold text-muted/20 font-mono group-hover:text-primary/10 transition-colors duration-300">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-4 ${step.color} group-hover:scale-110 group-hover:shadow-lg transition-all duration-300`}>
                      <Icon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2 group-hover:text-primary transition-colors duration-300">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* AI Chat Promo */}
      <AIChatPromo />

      {/* ===== TRUST SECTION - MASSIVE ===== */}
      <section data-testid="trust-section" className="space-y-8 mb-12">
        {/* Section Header */}
        <div className="text-center">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            <Shield className="w-3 h-3 mr-1" />
            {isRTL ? 'شريكك في النجاح' : 'Your Partner in Success'}
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
            {isRTL ? 'لماذا تختارنا؟' : 'Why Choose Us?'}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {isRTL 
              ? 'منصة عالمية موثوقة، آلاف البائعين يحققون أرباحاً يومية، ونظام متكامل يعمل لأجلك 24/7. نحن لا نعدك بالنجاح — نحن نصنعه معك.'
              : 'A trusted global platform, thousands of sellers earning daily profits, and a complete system working for you 24/7. We don\'t promise success — we build it with you.'}
          </p>
        </div>

        {/* Verified Partners Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="trust-partners">
          {[
            { 
              name: 'Stripe',
              status: isRTL ? 'شريك دفع معتمد' : 'Verified Payment Partner',
              icon: CreditCard,
              color: 'text-[#635BFF]',
              bgColor: 'bg-[#635BFF]/10',
              borderColor: 'border-[#635BFF]/20'
            },
            { 
              name: 'PayPal',
              status: isRTL ? 'شريك رسمي' : 'Official Partner',
              icon: BadgeCheck,
              color: 'text-[#003087]',
              bgColor: 'bg-[#003087]/10',
              borderColor: 'border-[#003087]/20'
            },
            { 
              name: isRTL ? 'مسجّلة في UK' : 'UK Registered',
              status: isRTL ? 'شركة بريطانية رسمية' : 'Official UK Company',
              icon: Building2,
              color: 'text-red-500',
              bgColor: 'bg-red-500/10',
              borderColor: 'border-red-500/20'
            },
            { 
              name: 'USDT / TRC-20',
              status: isRTL ? 'دفع فوري بالكريبتو' : 'Instant Crypto Payments',
              icon: Lock,
              color: 'text-emerald-500',
              bgColor: 'bg-emerald-500/10',
              borderColor: 'border-emerald-500/20'
            },
          ].map((partner, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className={`bg-card border ${partner.borderColor} hover:shadow-lg transition-all duration-300`}>
                <CardContent className="p-5 text-center">
                  <div className={`w-12 h-12 rounded-xl ${partner.bgColor} flex items-center justify-center mx-auto mb-3`}>
                    <partner.icon className={`w-6 h-6 ${partner.color}`} />
                  </div>
                  <p className="font-heading font-bold text-sm mb-1">{partner.name}</p>
                  <div className="flex items-center justify-center gap-1">
                    <CheckCircle className="w-3 h-3 text-[hsl(var(--success))]" />
                    <p className="text-xs text-muted-foreground">{partner.status}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Company Info Card */}
        <Card className="bg-card border-border overflow-hidden" data-testid="trust-company-info">
          <CardContent className="p-0">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: Company Details */}
              <div className="p-8 sm:p-10 space-y-6">
                <div>
                  <h3 className="font-heading text-2xl font-bold mb-2">
                    {isRTL ? 'شركة مسجّلة رسمياً' : 'Officially Registered Company'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {isRTL 
                      ? 'SHOP & SHIP LTD مسجّلة في المملكة المتحدة تحت رقم تسجيل رسمي، وتخضع لجميع القوانين واللوائح التجارية البريطانية.'
                      : 'SHOP & SHIP LTD is registered in the United Kingdom under an official registration number, subject to all British commercial laws and regulations.'}
                  </p>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: Building2, label: isRTL ? 'الاسم القانوني' : 'Legal Name', value: 'SHOP & SHIP LTD' },
                    { icon: MapPin, label: isRTL ? 'المقر الرئيسي' : 'Headquarters', value: isRTL ? 'لندن، المملكة المتحدة' : 'London, United Kingdom' },
                    { icon: Globe, label: isRTL ? 'نطاق العمل' : 'Operations', value: isRTL ? '32 دولة حول العالم' : '32 Countries Worldwide' },
                    { icon: Warehouse, label: isRTL ? 'المستودعات' : 'Warehouses', value: isRTL ? '8 مستودعات في 5 دول' : '8 Warehouses in 5 Countries' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{item.label}</p>
                        <p className="text-sm font-semibold">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Right: Team Photo */}
              <div className="relative min-h-[300px] lg:min-h-full">
                <img
                  src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW0lMjBvZmZpY2UlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzcxMzE0ODUxfDA&ixlib=rb-4.1.0&q=85"
                  alt={isRTL ? 'فريق SHOP & SHIP' : 'SHOP & SHIP Team'}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="trust-team-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-white font-heading font-bold text-lg">{isRTL ? 'فريقنا المحترف' : 'Our Professional Team'}</p>
                  <p className="text-white/70 text-sm">{isRTL ? '+45 موظف في 5 مكاتب حول العالم' : '45+ employees in 5 offices worldwide'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Warehouse Photos Grid */}
        <div data-testid="trust-warehouses">
          <h3 className="font-heading text-xl font-bold mb-5 text-center">
            {isRTL ? 'شبكة التخزين والشحن العالمية' : 'Global Storage & Shipping Network'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                img: 'https://images.unsplash.com/photo-1768796373360-95d80c5830fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBzaGlwcGluZyUyMGJveGVzfGVufDB8fHx8MTc3MTMxNDg0N3ww&ixlib=rb-4.1.0&q=85',
                location: isRTL ? 'لندن، المملكة المتحدة' : 'London, UK',
                size: isRTL ? '12,000 م²' : '12,000 m²'
              },
              {
                img: 'https://images.unsplash.com/photo-1768796373708-e1b62a0f2900?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBzaGlwcGluZyUyMGJveGVzfGVufDB8fHx8MTc3MTMxNDg0N3ww&ixlib=rb-4.1.0&q=85',
                location: isRTL ? 'دبي، الإمارات' : 'Dubai, UAE',
                size: isRTL ? '8,500 م²' : '8,500 m²'
              },
              {
                img: 'https://images.unsplash.com/photo-1685119166946-d4050647b0e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxlY29tbWVyY2UlMjBmdWxmaWxsbWVudCUyMGNlbnRlciUyMHBhY2thZ2VzJTIwcmVhZHklMjB0byUyMHNoaXB8ZW58MHx8fHwxNzcxMzE0ODU2fDA&ixlib=rb-4.1.0&q=85',
                location: isRTL ? 'إسطنبول، تركيا' : 'Istanbul, Turkey',
                size: isRTL ? '6,200 م²' : '6,200 m²'
              },
            ].map((wh, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.15 }}
                className="group relative rounded-xl overflow-hidden border border-border"
                data-testid={`trust-warehouse-${i}`}
              >
                <img src={wh.img} alt={wh.location} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-1.5 mb-1">
                    <MapPin className="w-3.5 h-3.5 text-primary" />
                    <p className="text-white text-sm font-semibold">{wh.location}</p>
                  </div>
                  <p className="text-white/60 text-xs">{wh.size}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Trust Badges Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="trust-badges">
          {[
            { icon: Shield, value: 'SSL 256-bit', label: isRTL ? 'تشفير كامل' : 'Full Encryption' },
            { icon: BadgeCheck, value: isRTL ? 'موثّق' : 'Verified', label: isRTL ? 'شركة موثّقة دولياً' : 'Company Identity Confirmed' },
            { icon: Users, value: '50,000+', label: isRTL ? 'بائع نشط' : 'Active Sellers' },
            { icon: Star, value: '4.8/5', label: isRTL ? 'تقييم العملاء' : 'Customer Rating' },
          ].map((badge, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="text-center p-5 rounded-xl bg-secondary/40 border border-border"
            >
              <badge.icon className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="font-mono text-lg font-bold mb-0.5">{badge.value}</p>
              <p className="text-xs text-muted-foreground">{badge.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== PACKAGING & QUALITY SECTION ===== */}
      <section data-testid="packaging-section" className="space-y-6 mb-12">
        <div className="text-center">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            <Package className="w-3 h-3 mr-1" />
            {isRTL ? 'تغليف وتخزين احترافي' : 'Professional Packaging & Storage'}
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
            {isRTL ? 'كيف نحمي منتجاتك؟' : 'How We Protect Your Products?'}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {isRTL 
              ? 'بضاعتك تصل للمشتري بأفضل حالة — نحن نتعامل مع كل طرد كأنه هدية ثمينة. من التخزين الذكي إلى التغليف المتعدد الطبقات، كل خطوة مدروسة بعناية'
              : 'Your products reach buyers in perfect condition — we treat every package like a precious gift. From smart storage to multi-layer packaging, every step is carefully planned'}
          </p>
        </div>

        {/* 4-Step Process */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {
              step: '01',
              icon: Warehouse,
              title: isRTL ? 'تخزين ذكي مُراقب' : 'Smart Monitored Storage',
              desc: isRTL ? 'منتجاتك تُخزّن في مستودعات مكيّفة بدرجة حرارة ورطوبة مثالية. كل منتج يحصل على رمز تتبع فريد من لحظة دخوله المستودع' : 'Products stored in climate-controlled warehouses with optimal temperature and humidity. Every product gets a unique tracking code from the moment it enters',
              color: 'text-primary',
              bg: 'bg-primary/10',
              border: 'border-primary/20'
            },
            {
              step: '02',
              icon: Scan,
              title: isRTL ? 'فحص جودة مزدوج' : 'Double Quality Check',
              desc: isRTL ? 'كل منتج يمر بفحصين: فحص عند الاستلام وفحص قبل الشحن. نتأكد من سلامة المنتج، اللون الصحيح، الحجم المطلوب، وعدم وجود أي عيوب' : 'Every product passes two inspections: upon receipt and before shipping. We verify integrity, correct color, required size, and no defects',
              color: 'text-amber-400',
              bg: 'bg-amber-500/10',
              border: 'border-amber-500/20'
            },
            {
              step: '03',
              icon: Package,
              title: isRTL ? 'تغليف متعدد الطبقات' : 'Multi-Layer Packaging',
              desc: isRTL ? 'تغليف من 3 طبقات: غلاف فقاعات هوائية للحماية من الصدمات، ورق حريري للمظهر الفاخر، ثم صندوق كرتون مقوّى مع ملصق SHOP & SHIP' : '3-layer packaging: bubble wrap for shock protection, tissue paper for luxury feel, then reinforced cardboard box with SHOP & SHIP label',
              color: 'text-emerald-400',
              bg: 'bg-emerald-500/10',
              border: 'border-emerald-500/20'
            },
            {
              step: '04',
              icon: ShieldCheck,
              title: isRTL ? 'شحن مؤمّن + تتبع' : 'Insured Shipping + Tracking',
              desc: isRTL ? 'كل شحنة مؤمّنة بالكامل ضد الضرر والفقدان. يحصل المشتري على رقم تتبع فوري ويمكنه متابعة طرده لحظة بلحظة حتى باب منزله' : 'Every shipment fully insured against damage and loss. Buyer gets instant tracking number and can follow their package step by step to their door',
              color: 'text-blue-400',
              bg: 'bg-blue-500/10',
              border: 'border-blue-500/20'
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.12 }}
            >
              <Card className={`bg-card border ${item.border} h-full hover:shadow-lg transition-all duration-300`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center`}>
                      <item.icon className={`w-5 h-5 ${item.color}`} />
                    </div>
                    <span className="font-mono text-2xl font-bold text-muted-foreground/15">{item.step}</span>
                  </div>
                  <h3 className="font-heading font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Packaging Photos */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              img: 'https://images.pexels.com/photos/6169643/pexels-photo-6169643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
              label: isRTL ? 'فحص الجودة قبل الشحن' : 'Quality Check Before Shipping',
              desc: isRTL ? 'فريق مختص يفحص كل منتج' : 'Specialized team inspects every product'
            },
            {
              img: 'https://images.pexels.com/photos/9594426/pexels-photo-9594426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
              label: isRTL ? 'تغليف فاخر ومحكم' : 'Premium Secure Packaging',
              desc: isRTL ? 'كل منتج يُغلّف بعناية فائقة' : 'Every product carefully wrapped'
            },
            {
              img: 'https://images.unsplash.com/photo-1631010231931-d2c396b444ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjB3b3JrZXIlMjBxdWFsaXR5JTIwaW5zcGVjdGlvbiUyMGNoZWNraW5nJTIwcGFja2FnZSUyMHNoaXBwaW5nJTIwbGFiZWx8ZW58MHx8fHwxNzcxMzgyNTcwfDA&ixlib=rb-4.1.0&q=85',
              label: isRTL ? 'جاهز للشحن الدولي' : 'Ready for International Shipping',
              desc: isRTL ? 'صناديق مقوّاة بملصق تتبع' : 'Reinforced boxes with tracking label'
            },
          ].map((photo, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.15 }}
              className="group relative rounded-xl overflow-hidden border border-border"
              data-testid={`packaging-photo-${i}`}
            >
              <img src={photo.img} alt={photo.label} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white text-sm font-semibold mb-0.5">{photo.label}</p>
                <p className="text-white/60 text-xs">{photo.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Guarantee Banner */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 text-center sm:text-right">
              <h3 className="font-heading text-lg font-bold mb-1">
                {isRTL ? 'ضمان وصول المنتج بحالة ممتازة أو استرداد فوري' : '100% Perfect Condition Guarantee or Instant Refund'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isRTL 
                  ? 'إذا وصل المنتج للمشتري بأي ضرر أو عيب، نتحمّل نحن المسؤولية الكاملة — نعيد المبلغ أو نرسل بديلاً فوراً. سمعتك كبائع محمية معنا'
                  : 'If the product reaches the buyer with any damage or defect, we take full responsibility — we refund or send a replacement immediately. Your reputation as a seller is protected with us'}
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Success Stories */}
      <section data-testid="success-stories" className="mb-12">
        <div className="text-center mb-8">
          <h2 className="font-heading text-3xl font-bold mb-3">{isRTL ? 'قصص نجاح بائعينا' : 'Seller Success Stories'}</h2>
          <p className="text-muted-foreground">{isRTL ? 'اكتشف كيف غيّر بائعونا حياتهم مع SHOP & SHIP' : 'Discover how our sellers transformed their lives with SHOP & SHIP'}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {successStories.map((story, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }}>
              <Card className="bg-card border-border h-full">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex gap-0.5 mb-3">
                    {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-current text-amber-400" />)}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">"{isRTL ? story.quote_ar : story.quote}"</p>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-0 font-mono text-sm px-3 py-1">
                      {story.earnings}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{isRTL ? 'خلال' : 'in'} {isRTL ? story.period_ar : story.period}</span>
                    <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">{isRTL ? story.level_ar : story.level}</Badge>
                  </div>
                  <div className="flex items-center gap-3 pt-3 border-t border-border">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${story.color || 'from-primary to-emerald-500'} flex items-center justify-center text-white font-bold text-sm`}>
                      {story.avatar}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{isRTL ? story.name_ar : story.name}</p>
                      <p className="text-xs text-muted-foreground">{isRTL ? story.country_ar : story.country}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="mb-12">
        <div className="text-center mb-8">
          <h2 className="font-heading text-3xl font-bold mb-3">{isRTL ? 'لماذا SHOP & SHIP؟' : 'Why SHOP & SHIP?'}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { num: '50,000+', label: isRTL ? 'منتج متاح' : 'Products Available', desc: isRTL ? 'من مصادر عالمية موثوقة' : 'From 6 global markets' },
            { num: '0$', label: isRTL ? 'تكلفة التخزين' : 'Storage Cost', desc: isRTL ? 'نخزن مجاناً في مستودعاتنا' : 'Free storage in our warehouses' },
            { num: '0$', label: isRTL ? 'تكلفة الشحن' : 'Shipping Cost', desc: isRTL ? 'نشحن مجاناً للعميل' : 'Free shipping to customer' },
            { num: '50%+', label: isRTL ? 'ربح مضمون' : 'Guaranteed Profit', desc: isRTL ? 'على كل منتج تبيعه' : 'On every product you sell' },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <Card className="bg-card border-border text-center">
                <CardContent className="p-5">
                  <p className="font-mono text-3xl font-bold text-primary mb-1">{item.num}</p>
                  <p className="text-sm font-semibold mb-1">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="mb-8">
        <Card className="bg-gradient-to-br from-primary/10 to-emerald-500/5 border-primary/20">
          <CardContent className="p-8 sm:p-12 text-center">
            <h2 className="font-heading text-3xl font-bold mb-4">{isRTL ? 'جاهز تبدأ رحلتك؟' : 'Ready to Start Your Journey?'}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              {isRTL ? 'سجل الآن مجاناً وابدأ في كسب العمولات من أول يوم. لا تحتاج خبرة سابقة — نحن هنا لمساعدتك!' : 'Register now for free and start earning commissions from day one. No prior experience needed — we are here to help!'}
            </p>
            <Button size="lg" onClick={() => navigate(isAuthenticated ? '/products' : '/register')} className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-base rounded-xl">
              <Zap className="w-5 h-5" />
              {isRTL ? 'سجل مجاناً وابدأ الآن' : 'Register Free & Start Now'}
            </Button>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
